import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { parseHex, SLATE_900, SLATE_100, toHex, contrastRatio } from '@respectly/shared-types';
export { adjustForContrast, getContrastRatio, getReadableTextColor, isLightColor, mixColors } from '@respectly/shared-types';
import * as React13 from 'react';
import { useState, useEffect } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { motion, useDragControls, AnimatePresence } from 'framer-motion';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Minus, Check, Circle, ChevronDown, ChevronUp, Lock, RotateCw, Signal, Wifi, BatteryFull, Info, AlertTriangle, AlertCircle, CheckCircle2, Search, ChevronsUpDown, ChevronLeft, ChevronRight, LayoutGrid, List, Plus, PanelLeftOpen, PanelLeftClose, ArrowLeft, Loader2, Command, Users, FileText, ClipboardList, ArrowRight, Bell, HelpCircle, User, Settings, LogOut, Home, MoreHorizontal, Bot, Smartphone, Ban, Mail, UserCheck, ShieldCheck, Repeat, Calendar, ShieldBan, Database, Shield, Clock, Send, BookOpen, MessageSquare, Phone, FolderOpen, Inbox, FileQuestion } from 'lucide-react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import * as DropdownMenuPrimitive2 from '@radix-ui/react-dropdown-menu';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as Popover from '@radix-ui/react-popover';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

// src/lib/utils.ts
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var tagColorCache = /* @__PURE__ */ new Map();
function getAccessibleTagColors(hexColor, opts = {}) {
  const { fillAlpha = 0.125, target = 4.5 } = opts;
  const cacheKey = `${hexColor}|${fillAlpha}|${target}`;
  const cached = tagColorCache.get(cacheKey);
  if (cached) return cached;
  const hue = parseHex(hexColor);
  if (!hue) return { backgroundColor: SLATE_100, color: SLATE_900 };
  const backgroundColor = toHex(hue.map((c) => fillAlpha * c + (1 - fillAlpha) * 255));
  const fill = parseHex(backgroundColor);
  let result = { backgroundColor, color: SLATE_900 };
  for (let k = 1; k >= 0.05; k -= 0.01) {
    const color = toHex(hue.map((c) => c * k));
    if (contrastRatio(parseHex(color), fill) >= target) {
      result = { backgroundColor, color };
      break;
    }
  }
  tagColorCache.set(cacheKey, result);
  return result;
}

// src/lib/avatar.ts
var AVATAR_COLORS = [
  "bg-blue-600",
  //   5.1:1
  "bg-green-700",
  //  4.9:1  (green-500/600 fail with white)
  "bg-purple-600",
  // 5.4:1
  "bg-orange-700",
  // 5.1:1  (orange-500/600 fail with white)
  "bg-pink-700",
  //   5.9:1
  "bg-teal-700",
  //   5.5:1  (teal-500/600 fail with white)
  "bg-indigo-600",
  // 6.3:1
  "bg-rose-700"
  //   6.4:1
];
function getAvatarColor(name) {
  const code = name?.charCodeAt(0) ?? 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length] ?? "bg-blue-600";
}
function getAvatarInitials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// src/lib/chart-a11y.ts
function chartA11yProps(summary, description) {
  return {
    role: "img",
    "aria-label": summary,
    title: summary,
    desc: description ?? summary
  };
}
function pieSegmentA11yProps(label) {
  return { "aria-label": label };
}
var buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap",
    "text-sm font-medium",
    "rounded-lg",
    "ring-offset-background transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:ring-offset-0",
    "disabled:pointer-events-none disabled:opacity-50",
    "min-h-touch"
  ].join(" "),
  {
    variants: {
      variant: {
        // Primary - matches auth pages: dark button in light mode, light in dark
        default: [
          "bg-slate-900 text-white",
          "hover:bg-slate-800",
          "dark:bg-white dark:text-slate-900",
          "dark:hover:bg-slate-100",
          "shadow-sm"
        ].join(" "),
        // Destructive - refined red
        destructive: [
          "bg-red-500 text-white",
          "hover:bg-red-600",
          "shadow-sm"
        ].join(" "),
        // Outline - subtle border matching auth pages
        outline: [
          "border border-slate-200 bg-white text-slate-700",
          "hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300",
          "dark:border-slate-700 dark:bg-transparent dark:text-slate-300",
          "dark:hover:bg-slate-800 dark:hover:text-white dark:hover:border-slate-600"
        ].join(" "),
        // Secondary - subtle fill
        secondary: [
          "bg-slate-100 text-slate-900",
          "hover:bg-slate-200",
          "dark:bg-slate-800 dark:text-slate-100",
          "dark:hover:bg-slate-700"
        ].join(" "),
        // Ghost - minimal style
        ghost: [
          "text-slate-600",
          "hover:bg-slate-100 hover:text-slate-900",
          "dark:text-slate-400",
          "dark:hover:bg-slate-800 dark:hover:text-white"
        ].join(" "),
        // Link - underlined text
        link: [
          "text-primary underline-offset-4",
          "hover:underline"
        ].join(" ")
      },
      size: {
        default: "h-11 px-5 py-2",
        sm: "h-9 px-4 text-sm",
        lg: "h-12 px-8 text-base",
        icon: "h-11 w-11"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React13.forwardRef(
  ({ className, variant, size, asChild = false, isLoading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        disabled: disabled || isLoading,
        ...props,
        children: isLoading ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "-ml-1 mr-2 h-4 w-4 animate-spin",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          "Loading..."
        ] }) : children
      }
    );
  }
);
Button.displayName = "Button";
var MotionButton = React13.forwardRef(
  ({ className, variant, size, isLoading, children, disabled, type = "button", onClick, whileTap, whileHover }, ref) => {
    return /* @__PURE__ */ jsx(
      motion.button,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        type,
        onClick,
        disabled: disabled || isLoading,
        whileTap: whileTap ?? { scale: 0.98 },
        whileHover,
        transition: { duration: 0.1 },
        children: isLoading ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "-ml-1 mr-2 h-4 w-4 animate-spin",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          "Loading..."
        ] }) : children
      }
    );
  }
);
MotionButton.displayName = "MotionButton";
var Input = React13.forwardRef(
  ({ className, type, error, leftIcon, rightIcon, ...props }, ref) => {
    const hasLeftIcon = !!leftIcon;
    const hasRightIcon = !!rightIcon;
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        leftIcon && /* @__PURE__ */ jsx("div", { className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400", children: leftIcon }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type,
            className: cn(
              // Base styles matching auth pages
              "flex h-11 w-full rounded-lg",
              "border border-slate-200 bg-white",
              "px-3 py-2 text-sm text-slate-900",
              "placeholder:text-slate-400",
              // Focus styles matching auth pages
              "focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
              // Disabled styles
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
              // Transition
              "transition-all duration-200",
              // Dark mode
              "dark:border-slate-700 dark:bg-slate-900 dark:text-white",
              "dark:placeholder:text-slate-500",
              "dark:focus:border-blue-500 dark:focus:ring-blue-500/20",
              "dark:disabled:bg-slate-800",
              // File input styles
              "file:border-0 file:bg-transparent file:text-sm file:font-medium",
              // Error state
              error && "border-red-500 focus:border-red-500 focus:ring-red-500/20",
              // Icon padding
              hasLeftIcon && "pl-10",
              hasRightIcon && "pr-10",
              className
            ),
            ref,
            ...props
          }
        ),
        rightIcon && /* @__PURE__ */ jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2 text-slate-400", children: rightIcon })
      ] }),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-red-500 dark:text-red-400", children: error })
    ] });
  }
);
Input.displayName = "Input";
var Textarea = React13.forwardRef(
  ({ className, error, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      /* @__PURE__ */ jsx(
        "textarea",
        {
          className: cn(
            // Base styles matching auth pages
            "flex min-h-[120px] w-full rounded-lg",
            "border border-slate-200 bg-white",
            "px-3 py-2.5 text-sm text-slate-900",
            "placeholder:text-slate-400",
            // Focus styles
            "focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
            // Disabled styles
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
            // Transition
            "transition-all duration-200",
            // Dark mode
            "dark:border-slate-700 dark:bg-slate-900 dark:text-white",
            "dark:placeholder:text-slate-500",
            "dark:focus:border-blue-500 dark:focus:ring-blue-500/20",
            "dark:disabled:bg-slate-800",
            // Resize
            "resize-none",
            // Error state
            error && "border-red-500 focus:border-red-500 focus:ring-red-500/20",
            className
          ),
          ref,
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-red-500 dark:text-red-400", children: error })
    ] });
  }
);
Textarea.displayName = "Textarea";
var Card = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "rounded-xl border border-slate-200 bg-white text-slate-900",
        "shadow-sm",
        "dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-100",
        className
      ),
      ...props
    }
  )
);
Card.displayName = "Card";
var CardHeader = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex flex-col space-y-1.5 p-6", className), ...props })
);
CardHeader.displayName = "CardHeader";
var CardTitle = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "h3",
    {
      ref,
      className: cn(
        "text-lg font-semibold leading-none tracking-tight text-slate-900",
        "dark:text-white",
        className
      ),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "p",
  {
    ref,
    className: cn("text-sm text-slate-500 dark:text-slate-400", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "flex items-center p-6 pt-0",
        className
      ),
      ...props
    }
  )
);
CardFooter.displayName = "CardFooter";
var Avatar = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className),
    ...props
  }
));
Avatar.displayName = AvatarPrimitive.Root.displayName;
var AvatarImage = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Image,
  {
    ref,
    className: cn("aspect-square h-full w-full", className),
    ...props
  }
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
var badgeVariants = cva(
  [
    "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
    "transition-colors duration-200",
    "focus:outline-none focus:ring-2 focus:ring-ring/20 focus:ring-offset-0"
  ].join(" "),
  {
    variants: {
      variant: {
        // Default - slate-based
        default: [
          "border-transparent bg-slate-900 text-white",
          "dark:bg-white dark:text-slate-900"
        ].join(" "),
        // Secondary - subtle fill
        secondary: [
          "border-transparent bg-slate-100 text-slate-700",
          "dark:bg-slate-800 dark:text-slate-300"
        ].join(" "),
        // Destructive - refined red
        destructive: [
          "border-transparent bg-red-100 text-red-700",
          "dark:bg-red-900/30 dark:text-red-400"
        ].join(" "),
        // Outline - bordered
        outline: [
          "border-slate-200 text-slate-700 bg-transparent",
          "dark:border-slate-700 dark:text-slate-300"
        ].join(" "),
        // Success - green
        success: [
          "border-transparent bg-emerald-100 text-emerald-700",
          "dark:bg-emerald-900/30 dark:text-emerald-400"
        ].join(" "),
        // Warning - amber
        warning: [
          "border-transparent bg-amber-100 text-amber-700",
          "dark:bg-amber-900/30 dark:text-amber-400"
        ].join(" "),
        // Error - red (alias for destructive)
        error: [
          "border-transparent bg-red-100 text-red-700",
          "dark:bg-red-900/30 dark:text-red-400"
        ].join(" "),
        // Info - blue
        info: [
          "border-transparent bg-blue-100 text-blue-700",
          "dark:bg-blue-900/30 dark:text-blue-400"
        ].join(" ")
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn(badgeVariants({ variant }), className), ...props });
}
var statusConfig = {
  pending: { variant: "warning", label: "Pending", color: "bg-amber-500" },
  sent: { variant: "info", label: "Sent", color: "bg-blue-500" },
  delivered: { variant: "success", label: "Delivered", color: "bg-emerald-500" },
  opened: { variant: "secondary", label: "Opened", color: "bg-violet-500" },
  signed: { variant: "success", label: "Signed", color: "bg-emerald-500" },
  failed: { variant: "destructive", label: "Failed", color: "bg-red-500" },
  expired: { variant: "outline", label: "Expired", color: "bg-slate-400" }
};
function StatusBadge({ status, className, ...props }) {
  const config = statusConfig[status];
  return /* @__PURE__ */ jsxs(Badge, { variant: config.variant, className: cn("gap-1", className), ...props, children: [
    /* @__PURE__ */ jsx("span", { className: cn("h-1.5 w-1.5 rounded-full", config.color) }),
    config.label
  ] });
}
var sizeClasses = {
  sm: "h-4 w-4 border-2",
  md: "h-6 w-6 border-2",
  lg: "h-8 w-8 border-3"
};
function Spinner({ size = "md", className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "animate-spin rounded-full border-primary border-t-transparent",
        sizeClasses[size],
        className
      ),
      ...props
    }
  );
}
function LoadingScreen({ message = "Loading..." }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex min-h-screen flex-col items-center justify-center", children: [
    /* @__PURE__ */ jsx(Spinner, { size: "lg" }),
    /* @__PURE__ */ jsx("p", { className: "mt-4 text-sm text-muted-foreground", children: message })
  ] });
}
function LoadingInline({ message = "Loading..." }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-muted-foreground", children: [
    /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
    /* @__PURE__ */ jsx("span", { className: "text-sm", children: message })
  ] });
}
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("animate-pulse rounded-md bg-muted", className), ...props });
}
function SkeletonText({ width = "w-full" }) {
  return /* @__PURE__ */ jsx(Skeleton, { className: cn("h-4", width) });
}
function SkeletonAvatar({ size = "md" }) {
  const sizeClasses2 = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12"
  };
  return /* @__PURE__ */ jsx(Skeleton, { className: cn("rounded-full", sizeClasses2[size]) });
}
function SkeletonCard() {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3 rounded-lg border p-4", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-3/4" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-1/2" }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 pt-2", children: [
      /* @__PURE__ */ jsx(SkeletonAvatar, { size: "sm" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-24" })
    ] })
  ] });
}
function SkeletonTableRow({ columns = 4 }) {
  return /* @__PURE__ */ jsx("div", { className: "flex items-center gap-4 py-3", children: Array.from({ length: columns }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: cn("h-4", i === 0 ? "w-1/4" : "w-1/6") }, i)) });
}
function SkeletonListItem() {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 py-3", children: [
    /* @__PURE__ */ jsx(SkeletonAvatar, { size: "md" }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-3/4" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-1/2" })
    ] })
  ] });
}
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const listener = (event) => {
      setMatches(event.matches);
    };
    media.addEventListener("change", listener);
    return () => {
      media.removeEventListener("change", listener);
    };
  }, [query]);
  return matches;
}

// src/hooks/useIsMobile.ts
function useIsMobile() {
  return !useMediaQuery("(min-width: 768px)");
}
function useIsTablet() {
  return !useMediaQuery("(min-width: 1024px)");
}
function useIsDesktop() {
  return useMediaQuery("(min-width: 1024px)");
}

// src/lib/animations.ts
var defaultTransition = {
  type: "spring",
  damping: 25,
  stiffness: 200
};
var fastTransition = {
  duration: 0.15,
  ease: "easeOut"
};
var pageTransitionVariants = {
  mobile: {
    initial: { x: "100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "-100%", opacity: 0 }
  },
  desktop: {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 }
  }
};
var staggerContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03
    }
  }
};
var staggerItemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.12
    }
  }
};
var bottomSheetVariants = {
  hidden: { y: "100%" },
  visible: {
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    y: "100%",
    transition: {
      duration: 0.2
    }
  }
};
var modalVariants = {
  hidden: { opacity: 0, scale: 0.95, x: "-50%", y: "-50%" },
  visible: {
    opacity: 1,
    scale: 1,
    x: "-50%",
    y: "-50%",
    transition: {
      duration: 0.2,
      ease: [0.16, 1, 0.3, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    x: "-50%",
    y: "-50%",
    transition: {
      duration: 0.15,
      ease: "easeIn"
    }
  }
};
var overlayVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  }
};
var buttonPressVariants = {
  mobile: {
    whileTap: { scale: 0.97 }
  },
  desktop: {
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 }
  }
};
var toastVariants = {
  mobile: {
    initial: { y: 100, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: 100, opacity: 0 }
  },
  desktop: {
    initial: { x: 50, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 50, opacity: 0 }
  }
};
var fabVariants = {
  hidden: { scale: 0, rotate: -180 },
  visible: {
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20
    }
  }
};
var fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  }
};
var slideUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  }
};
var collapseVariants = {
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      height: {
        duration: 0.2
      },
      opacity: {
        duration: 0.15,
        delay: 0.05
      }
    }
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      height: {
        duration: 0.2
      },
      opacity: {
        duration: 0.1
      }
    }
  }
};
var searchDropdownVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.96,
    transition: { duration: 0.1 }
  }
};
var searchOverlayVariants = {
  hidden: { y: "-100%" },
  visible: {
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    y: "-100%",
    transition: { duration: 0.2 }
  }
};
var searchResultsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05
    }
  }
};
var searchResultItemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.15 }
  }
};
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React13.forwardRef(({ className }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Overlay, { ref, asChild: true, children: /* @__PURE__ */ jsx(
  motion.div,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm",
      className
    ),
    variants: overlayVariants,
    initial: "hidden",
    animate: "visible",
    exit: "exit"
  }
) }));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React13.forwardRef(({ className, children, showCloseButton = true, preventClose = false, ...props }, ref) => {
  const isMobile = useIsMobile();
  const dragControls = useDragControls();
  const [, setIsDragging] = React13.useState(false);
  const handleDragEnd = (_event, info) => {
    setIsDragging(false);
    if (!preventClose && (info.offset.y > 100 || info.velocity.y > 500)) {
      const closeButton = document.querySelector("[data-dialog-close]");
      if (closeButton instanceof HTMLElement) {
        closeButton.click();
      }
    }
  };
  return /* @__PURE__ */ jsx(DialogPortal, { children: /* @__PURE__ */ jsxs(AnimatePresence, { mode: "wait", children: [
    /* @__PURE__ */ jsx(DialogOverlay, {}, "dialog-overlay"),
    /* @__PURE__ */ jsx(DialogPrimitive.Content, { ref, asChild: true, ...props, children: isMobile ? (
      // Mobile: Bottom Sheet
      /* @__PURE__ */ jsxs(
        motion.div,
        {
          className: cn(
            "fixed inset-x-0 bottom-0 z-50 max-h-[90vh] rounded-t-2xl bg-background shadow-lg",
            "flex flex-col overflow-hidden",
            className
          ),
          variants: bottomSheetVariants,
          initial: "hidden",
          animate: "visible",
          exit: "exit",
          drag: preventClose ? false : "y",
          dragControls,
          dragListener: false,
          dragConstraints: { top: 0 },
          dragElastic: 0.2,
          onDragStart: () => setIsDragging(true),
          onDragEnd: handleDragEnd,
          children: [
            !preventClose && /* @__PURE__ */ jsx(
              "div",
              {
                className: "flex justify-center py-3 cursor-grab active:cursor-grabbing touch-pan-y",
                onPointerDown: (e) => dragControls.start(e),
                children: /* @__PURE__ */ jsx("div", { className: "h-1.5 w-12 rounded-full bg-muted-foreground/30" })
              }
            ),
            /* @__PURE__ */ jsx("div", { className: cn(
              "flex-1 overflow-auto px-4 pb-safe",
              !preventClose && "pt-0",
              preventClose && "pt-4"
            ), children }),
            showCloseButton && /* @__PURE__ */ jsxs(
              DialogPrimitive.Close,
              {
                "data-dialog-close": true,
                className: "absolute right-4 top-4 rounded-full p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
                children: [
                  /* @__PURE__ */ jsx(X, { className: "h-5 w-5" }),
                  /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
                ]
              }
            )
          ]
        }
      )
    ) : (
      // Desktop: Center Modal
      // Note: translate is handled by framer-motion variants, not Tailwind (they conflict)
      /* @__PURE__ */ jsxs(
        motion.div,
        {
          className: cn(
            "fixed left-[50%] top-[50%] z-50 w-full max-w-lg",
            "rounded-lg border bg-background p-6 shadow-xl",
            className
          ),
          variants: modalVariants,
          initial: "hidden",
          animate: "visible",
          exit: "exit",
          children: [
            children,
            showCloseButton && /* @__PURE__ */ jsxs(
              DialogPrimitive.Close,
              {
                "data-dialog-close": true,
                className: "absolute right-4 top-4 rounded-full p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
                children: [
                  /* @__PURE__ */ jsx(X, { className: "h-5 w-5" }),
                  /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
                ]
              }
            )
          ]
        }
      )
    ) }, "dialog-content")
  ] }) });
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end sm:gap-3",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
function Modal({ isOpen, onClose, size = "default", title, children }) {
  const sizeClasses2 = {
    default: "max-w-lg",
    large: "max-w-3xl",
    fullscreen: "max-w-full h-screen m-0 rounded-none"
  };
  return /* @__PURE__ */ jsx(Dialog, { open: isOpen, onOpenChange: (open) => !open && onClose(), children: /* @__PURE__ */ jsxs(DialogContent, { className: sizeClasses2[size], children: [
    title && /* @__PURE__ */ jsx(DialogHeader, { children: /* @__PURE__ */ jsx(DialogTitle, { children: title }) }),
    children
  ] }) });
}
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React13.forwardRef(({ className, ...props }, ref) => {
  const isMobile = useIsMobile();
  return /* @__PURE__ */ jsx(
    ToastPrimitives.Viewport,
    {
      ref,
      className: cn(
        "fixed z-[100] flex max-h-screen flex-col-reverse gap-2 p-4",
        // Mobile: bottom center
        isMobile && "inset-x-0 bottom-0 pb-safe",
        // Desktop: top right
        !isMobile && "right-0 top-0 w-full max-w-[420px]",
        className
      ),
      ...props
    }
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants2 = cva(
  [
    "group pointer-events-auto relative flex w-full items-center justify-between gap-4",
    "overflow-hidden rounded-xl border p-4 pr-8 shadow-lg",
    "transition-all duration-200 min-h-touch"
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "border-slate-200 bg-white text-slate-900",
          "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        ].join(" "),
        success: [
          "border-emerald-200 bg-emerald-50 text-emerald-900",
          "dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100"
        ].join(" "),
        error: [
          "border-red-200 bg-red-50 text-red-900",
          "dark:border-red-800 dark:bg-red-950 dark:text-red-100"
        ].join(" "),
        warning: [
          "border-amber-200 bg-amber-50 text-amber-900",
          "dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        ].join(" "),
        info: [
          "border-blue-200 bg-blue-50 text-blue-900",
          "dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100"
        ].join(" ")
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Toast = React13.forwardRef(({ className, variant, children, ...props }, ref) => {
  const isMobile = useIsMobile();
  const animationVariant = isMobile ? toastVariants.mobile : toastVariants.desktop;
  return /* @__PURE__ */ jsx(ToastPrimitives.Root, { ref, asChild: true, ...props, children: /* @__PURE__ */ jsx(
    motion.div,
    {
      className: cn(toastVariants2({ variant }), className),
      initial: animationVariant.initial,
      animate: animationVariant.animate,
      exit: animationVariant.exit,
      transition: { duration: 0.2 },
      layout: true,
      children
    }
  ) });
});
Toast.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Action,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center",
      "rounded-lg border bg-transparent px-3 text-sm font-medium",
      "transition-colors duration-200",
      "hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
      "disabled:pointer-events-none disabled:opacity-50",
      "dark:hover:bg-slate-800",
      // Variant-specific styling
      "group-[.success]:border-emerald-300 group-[.success]:hover:border-emerald-400 group-[.success]:hover:bg-emerald-100",
      "group-[.error]:border-red-300 group-[.error]:hover:border-red-400 group-[.error]:hover:bg-red-100",
      "group-[.warning]:border-amber-300 group-[.warning]:hover:border-amber-400 group-[.warning]:hover:bg-amber-100",
      "group-[.info]:border-blue-300 group-[.info]:hover:border-blue-400 group-[.info]:hover:bg-blue-100",
      className
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Close,
  {
    ref,
    className: cn(
      "absolute right-2 top-2 rounded-lg p-1.5 opacity-0",
      "transition-all duration-200",
      "hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
      "group-hover:opacity-100",
      "text-slate-500 hover:text-slate-900 hover:bg-slate-100",
      "dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800",
      // Variant-specific styling
      "group-[.success]:text-emerald-600 group-[.success]:hover:text-emerald-900 group-[.success]:hover:bg-emerald-100",
      "group-[.error]:text-red-600 group-[.error]:hover:text-red-900 group-[.error]:hover:bg-red-100",
      "group-[.warning]:text-amber-600 group-[.warning]:hover:text-amber-900 group-[.warning]:hover:bg-amber-100",
      "group-[.info]:text-blue-600 group-[.info]:hover:text-blue-900 group-[.info]:hover:bg-blue-100",
      className
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("text-sm font-semibold", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("text-sm opacity-90", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;
var ToastIcon = ({ variant }) => {
  const iconClass = "h-5 w-5 shrink-0";
  switch (variant) {
    case "success":
      return /* @__PURE__ */ jsx(CheckCircle2, { className: cn(iconClass, "text-emerald-600 dark:text-emerald-400") });
    case "error":
      return /* @__PURE__ */ jsx(AlertCircle, { className: cn(iconClass, "text-red-600 dark:text-red-400") });
    case "warning":
      return /* @__PURE__ */ jsx(AlertTriangle, { className: cn(iconClass, "text-amber-600 dark:text-amber-400") });
    case "info":
      return /* @__PURE__ */ jsx(Info, { className: cn(iconClass, "text-blue-600 dark:text-blue-400") });
    default:
      return null;
  }
};
var TOAST_LIMIT = 5;
var TOAST_REMOVE_DELAY = 5e3;
var count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
var toastTimeouts = /* @__PURE__ */ new Map();
var addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
var reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
var listeners = [];
var memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
}
toast.success = (title, description) => toast({ variant: "success", title, description });
toast.error = (title, description) => toast({ variant: "error", title, description });
toast.warning = (title, description) => toast({ variant: "warning", title, description });
toast.info = (title, description) => toast({ variant: "info", title, description });
function useToast() {
  const [state, setState] = React13.useState(memoryState);
  React13.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}
function ToastAnnouncer({ toasts }) {
  const [polite, setPolite] = React13.useState(null);
  const [assertive, setAssertive] = React13.useState(null);
  const lastAnnouncedId = React13.useRef(null);
  const latest = toasts[0];
  React13.useEffect(() => {
    if (!latest || latest.id === lastAnnouncedId.current) return;
    lastAnnouncedId.current = latest.id;
    const text = /* @__PURE__ */ jsxs(Fragment, { children: [
      latest.title,
      latest.title && latest.description ? ". " : null,
      latest.description
    ] });
    if (latest.variant === "error") {
      setAssertive({ id: latest.id, text });
    } else {
      setPolite({ id: latest.id, text });
    }
  }, [latest]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "sr-only", role: "status", "aria-live": "polite", "aria-atomic": "true", children: polite && /* @__PURE__ */ jsx("span", { children: polite.text }, polite.id) }),
    /* @__PURE__ */ jsx("div", { className: "sr-only", role: "alert", "aria-live": "assertive", "aria-atomic": "true", children: assertive && /* @__PURE__ */ jsx("span", { children: assertive.text }, assertive.id) })
  ] });
}
function Toaster() {
  const { toasts } = useToast();
  return /* @__PURE__ */ jsxs(ToastProvider, { children: [
    /* @__PURE__ */ jsx(ToastAnnouncer, { toasts }),
    /* @__PURE__ */ jsx(AnimatePresence, { mode: "popLayout", children: toasts.map(function({ id, title, description, action, variant, ...props }) {
      return /* @__PURE__ */ jsxs(Toast, { variant, ...props, className: variant || "", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
          /* @__PURE__ */ jsx(ToastIcon, { variant }),
          /* @__PURE__ */ jsxs("div", { className: "grid gap-1", children: [
            title && /* @__PURE__ */ jsx(ToastTitle, { children: title }),
            description && /* @__PURE__ */ jsx(ToastDescription, { children: description })
          ] })
        ] }),
        action,
        /* @__PURE__ */ jsx(ToastClose, {})
      ] }, id);
    }) }),
    /* @__PURE__ */ jsx(ToastViewport, {})
  ] });
}
function DataTable({
  data,
  columns,
  renderColumnHeader,
  fillHeight = false,
  searchable = false,
  searchPlaceholder = "Search...",
  onSearch,
  searchValue = "",
  sortColumn,
  sortDirection,
  onSort,
  pagination = false,
  pageSize = 10,
  currentPage = 1,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  isLoading = false,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  getRowId,
  getRowLabel,
  selectAllLabel = "Select all",
  onRowClick,
  className,
  emptyState,
  mobileCard
}) {
  const isMobile = useIsMobile();
  const [localSearchValue, setLocalSearchValue] = React13.useState(searchValue);
  const handleSearch = React13.useCallback(
    (value) => {
      setLocalSearchValue(value);
      onSearch?.(value);
    },
    [onSearch]
  );
  const handleSort = (columnId) => {
    if (!onSort) return;
    let newDirection = "asc";
    if (sortColumn === columnId) {
      if (sortDirection === "asc") {
        newDirection = "desc";
      } else if (sortDirection === "desc") {
        newDirection = null;
      }
    }
    onSort(columnId, newDirection);
  };
  const handleSelectAll = (checked) => {
    if (!onSelectionChange || !getRowId) return;
    if (checked) {
      onSelectionChange(data.map(getRowId));
    } else {
      onSelectionChange([]);
    }
  };
  const handleSelectRow = (rowId, checked) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange([...selectedRows, rowId]);
    } else {
      onSelectionChange(selectedRows.filter((id) => id !== rowId));
    }
  };
  const getCellValue = (row, column) => {
    if (column.cell) {
      return column.cell(row);
    }
    if (column.accessorFn) {
      return column.accessorFn(row);
    }
    if (column.accessorKey) {
      return row[column.accessorKey];
    }
    return null;
  };
  const total = totalItems ?? data.length;
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, total);
  const visibleColumns = isMobile ? columns.filter((col) => !col.hideOnMobile) : columns;
  if (isLoading) {
    return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
      searchable && /* @__PURE__ */ jsx("div", { className: "relative", children: /* @__PURE__ */ jsx(Skeleton, { className: "h-11 w-full max-w-sm" }) }),
      /* @__PURE__ */ jsx("div", { className: "rounded-lg border", children: isMobile ? /* @__PURE__ */ jsx("div", { className: "space-y-2 p-4", children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-24 w-full rounded-lg" }, i)) }) : /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
        /* @__PURE__ */ jsx("div", { className: "bg-muted/50 border-b p-4", children: /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-full" }) }),
        Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx("div", { className: "border-b p-4 last:border-0", children: /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-full" }) }, i))
      ] }) })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("space-y-4", fillHeight && "flex h-full min-h-0 flex-col space-y-0", className),
      children: [
        searchable && /* @__PURE__ */ jsxs("div", { className: "relative max-w-sm", children: [
          /* @__PURE__ */ jsx(Search, { className: "text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" }),
          /* @__PURE__ */ jsx(
            Input,
            {
              placeholder: searchPlaceholder,
              value: localSearchValue,
              onChange: (e) => handleSearch(e.target.value),
              className: "pl-9 pr-9"
            }
          ),
          localSearchValue && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleSearch(""),
              className: "text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2",
              children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "bg-background rounded-lg border",
              fillHeight && "flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border-0"
            ),
            children: isMobile && mobileCard ? (
              // Mobile: Card View
              /* @__PURE__ */ jsx(
                motion.div,
                {
                  className: "divide-y",
                  variants: staggerContainerVariants,
                  initial: "hidden",
                  animate: "visible",
                  children: /* @__PURE__ */ jsx(AnimatePresence, { mode: "popLayout", children: data.length === 0 ? /* @__PURE__ */ jsx("div", { className: "text-muted-foreground p-8 text-center", children: emptyState || "No data available" }) : data.map((row, index) => /* @__PURE__ */ jsx(
                    motion.div,
                    {
                      variants: staggerItemVariants,
                      layout: true,
                      className: "p-4",
                      children: mobileCard(row, index)
                    },
                    getRowId ? getRowId(row) : index
                  )) })
                }
              )
            ) : (
              // Desktop: Table View
              /* @__PURE__ */ jsx("div", { className: cn("overflow-x-auto", fillHeight && "min-h-0 flex-1 overflow-auto"), children: /* @__PURE__ */ jsxs("table", { className: "min-w-full", children: [
                /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "bg-muted/50 border-b", children: [
                  selectable && /* @__PURE__ */ jsxs("th", { className: "w-12 p-4", onClick: (e) => e.stopPropagation(), children: [
                    /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Select column" }),
                    /* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "checkbox",
                        "aria-label": selectAllLabel,
                        checked: data.length > 0 && selectedRows.length === data.length,
                        onChange: (e) => handleSelectAll(e.target.checked),
                        className: "text-primary focus:ring-primary h-4 w-4 rounded border-gray-300"
                      }
                    )
                  ] }),
                  visibleColumns.map((column) => /* @__PURE__ */ jsx(
                    "th",
                    {
                      className: cn(
                        // slate-600 (not muted-foreground/slate-500): header text sits on the
                        // bg-muted/50 fill, where slate-500 is only 4.4:1 — below WCAG AA (#2193).
                        "p-4 text-left text-sm font-medium text-slate-600 dark:text-slate-400",
                        column.sortable && "hover:text-foreground cursor-pointer select-none",
                        column.headerClassName
                      ),
                      "aria-sort": sortColumn === column.id && sortDirection ? sortDirection === "asc" ? "ascending" : "descending" : void 0,
                      onClick: () => !renderColumnHeader && column.sortable && handleSort(column.id),
                      children: renderColumnHeader ? renderColumnHeader(column) : /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                        column.header ? column.header : column.srHeader && /* @__PURE__ */ jsx("span", { className: "sr-only", children: column.srHeader }),
                        column.sortable && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: sortColumn === column.id ? sortDirection === "asc" ? /* @__PURE__ */ jsx(ChevronUp, { className: "h-4 w-4" }) : sortDirection === "desc" ? /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(ChevronsUpDown, { className: "h-4 w-4 opacity-50" }) : /* @__PURE__ */ jsx(ChevronsUpDown, { className: "h-4 w-4 opacity-50" }) })
                      ] })
                    },
                    column.id
                  ))
                ] }) }),
                /* @__PURE__ */ jsx("tbody", { children: /* @__PURE__ */ jsx(AnimatePresence, { mode: "popLayout", children: data.length === 0 ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx(
                  "td",
                  {
                    colSpan: visibleColumns.length + (selectable ? 1 : 0),
                    className: "text-muted-foreground p-8 text-center",
                    children: emptyState || "No data available"
                  }
                ) }) : data.map((row, index) => {
                  const rowId = getRowId ? getRowId(row) : String(index);
                  const isSelected = selectedRows.includes(rowId);
                  return /* @__PURE__ */ jsxs(
                    motion.tr,
                    {
                      className: cn(
                        "border-b transition-colors last:border-0",
                        isSelected && "bg-primary/5",
                        !isSelected && "hover:bg-muted/50",
                        onRowClick && "cursor-pointer"
                      ),
                      initial: { opacity: 0, y: 8 },
                      animate: { opacity: 1, y: 0 },
                      exit: { opacity: 0, y: -8 },
                      transition: { duration: 0.15 },
                      layout: true,
                      onClick: () => onRowClick?.(row),
                      children: [
                        selectable && /* @__PURE__ */ jsx("td", { className: "w-12 p-4", onClick: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx(
                          "input",
                          {
                            type: "checkbox",
                            "aria-label": getRowLabel ? `Select ${getRowLabel(row)}` : "Select row",
                            checked: isSelected,
                            onChange: (e) => handleSelectRow(rowId, e.target.checked),
                            className: "text-primary focus:ring-primary h-4 w-4 rounded border-gray-300"
                          }
                        ) }),
                        visibleColumns.map((column, colIndex) => {
                          const content = getCellValue(row, column);
                          return /* @__PURE__ */ jsx("td", { className: cn("p-4 text-sm", column.className), children: onRowClick && colIndex === 0 ? /* @__PURE__ */ jsx(
                            "button",
                            {
                              type: "button",
                              onClick: (e) => {
                                e.stopPropagation();
                                onRowClick(row);
                              },
                              "aria-label": getRowLabel ? `Open ${getRowLabel(row)}` : "Open row",
                              className: "focus-visible:ring-primary flex w-full items-center gap-3 rounded-sm text-left focus:outline-none focus-visible:ring-2",
                              children: content
                            }
                          ) : content }, column.id);
                        })
                      ]
                    },
                    rowId
                  );
                }) }) })
              ] }) })
            )
          }
        ),
        pagination && totalPages > 1 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between", children: [
          /* @__PURE__ */ jsxs("div", { className: "text-muted-foreground text-sm", children: [
            "Showing ",
            startItem,
            " to ",
            endItem,
            " of ",
            total,
            " results"
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            onPageSizeChange && /* @__PURE__ */ jsx(
              "select",
              {
                value: pageSize,
                onChange: (e) => onPageSizeChange(Number(e.target.value)),
                className: "border-input bg-background h-9 rounded-md border px-3 text-sm",
                children: pageSizeOptions.map((size) => /* @__PURE__ */ jsxs("option", { value: size, children: [
                  size,
                  " per page"
                ] }, size))
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "outline",
                  size: "icon",
                  onClick: () => onPageChange?.(currentPage - 1),
                  disabled: currentPage <= 1,
                  "aria-label": "Previous page",
                  className: "h-9 w-9",
                  children: /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4", "aria-hidden": "true" })
                }
              ),
              /* @__PURE__ */ jsx("div", { className: "hidden items-center gap-1 sm:flex", children: generatePageNumbers(currentPage, totalPages).map((page, i) => /* @__PURE__ */ jsx(React13.Fragment, { children: page === "ellipsis" ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground px-2", children: "..." }) : /* @__PURE__ */ jsx(
                Button,
                {
                  variant: page === currentPage ? "default" : "outline",
                  size: "icon",
                  onClick: () => onPageChange?.(page),
                  className: "h-9 w-9",
                  children: page
                }
              ) }, i)) }),
              /* @__PURE__ */ jsxs("span", { className: "px-3 text-sm sm:hidden", children: [
                currentPage,
                " / ",
                totalPages
              ] }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "outline",
                  size: "icon",
                  onClick: () => onPageChange?.(currentPage + 1),
                  disabled: currentPage >= totalPages,
                  "aria-label": "Next page",
                  className: "h-9 w-9",
                  children: /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4", "aria-hidden": "true" })
                }
              )
            ] })
          ] })
        ] })
      ]
    }
  );
}
function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = [];
  pages.push(1);
  if (current <= 3) {
    pages.push(2, 3, 4, "ellipsis", total);
  } else if (current >= total - 2) {
    pages.push("ellipsis", total - 3, total - 2, total - 1, total);
  } else {
    pages.push("ellipsis", current - 1, current, current + 1, "ellipsis", total);
  }
  return pages;
}
function ViewToggle({
  view,
  onViewChange,
  className,
  disabled = false
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "inline-flex items-center rounded-lg border bg-muted p-1",
        disabled && "opacity-50 pointer-events-none",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(
          ViewToggleButton,
          {
            active: view === "grid",
            onClick: () => onViewChange("grid"),
            disabled,
            "aria-label": "Grid view",
            children: /* @__PURE__ */ jsx(LayoutGrid, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx(
          ViewToggleButton,
          {
            active: view === "list",
            onClick: () => onViewChange("list"),
            disabled,
            "aria-label": "List view",
            children: /* @__PURE__ */ jsx(List, { className: "h-4 w-4" })
          }
        )
      ]
    }
  );
}
function ViewToggleButton({
  active,
  onClick,
  disabled,
  children,
  "aria-label": ariaLabel
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      onClick,
      disabled,
      "aria-label": ariaLabel,
      "aria-pressed": active,
      className: cn(
        "relative inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
      ),
      children: [
        active && /* @__PURE__ */ jsx(
          motion.div,
          {
            layoutId: "viewToggleIndicator",
            className: "absolute inset-0 rounded-md bg-background shadow-sm",
            transition: { type: "spring", damping: 25, stiffness: 300 }
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "relative z-10", children })
      ]
    }
  );
}
function useViewToggle(key, defaultView = "list") {
  const [view, setView] = React13.useState(() => {
    if (typeof window === "undefined") return defaultView;
    const stored = localStorage.getItem(`view-toggle-${key}`);
    return stored || defaultView;
  });
  const setViewAndPersist = React13.useCallback(
    (newView) => {
      setView(newView);
      if (typeof window !== "undefined") {
        localStorage.setItem(`view-toggle-${key}`, newView);
      }
    },
    [key]
  );
  return [view, setViewAndPersist];
}
function TagBadge({
  tag,
  onRemove,
  size = "md",
  removable = false,
  className,
  onClick
}) {
  const chipColors = getAccessibleTagColors(tag.color, { fillAlpha: 0.15 });
  const sizeClasses2 = {
    sm: "text-xs px-2 py-0.5 gap-1",
    md: "text-sm px-2.5 py-1 gap-1.5",
    lg: "text-base px-3 py-1.5 gap-2"
  };
  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-3.5 w-3.5",
    lg: "h-4 w-4"
  };
  const handleRemove = (e) => {
    e.stopPropagation();
    onRemove?.(tag);
  };
  const isInteractive = onClick || removable;
  return /* @__PURE__ */ jsxs(
    motion.span,
    {
      initial: { scale: 0.8, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 0.8, opacity: 0 },
      transition: { duration: 0.15 },
      className: cn(
        "inline-flex items-center rounded-full font-medium",
        sizeClasses2[size],
        isInteractive && "cursor-pointer",
        className
      ),
      style: chipColors,
      onClick,
      whileHover: isInteractive ? { scale: 1.02 } : void 0,
      whileTap: isInteractive ? { scale: 0.98 } : void 0,
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            className: cn("rounded-full", size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2"),
            style: { backgroundColor: tag.color }
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "truncate max-w-[150px]", children: tag.name }),
        removable && onRemove && /* @__PURE__ */ jsx(
          "button",
          {
            onClick: handleRemove,
            className: "rounded-full p-0.5 hover:bg-black/10 focus:outline-none focus:ring-1 focus:ring-current",
            "aria-label": `Remove ${tag.name}`,
            children: /* @__PURE__ */ jsx(X, { className: iconSizes[size] })
          }
        )
      ]
    }
  );
}
function TagList({
  tags,
  onRemove,
  onTagClick,
  removable = false,
  size = "md",
  maxVisible,
  className
}) {
  const visibleTags = maxVisible ? tags.slice(0, maxVisible) : tags;
  const hiddenCount = maxVisible ? Math.max(0, tags.length - maxVisible) : 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap gap-2", className), children: [
    visibleTags.map((tag) => /* @__PURE__ */ jsx(
      TagBadge,
      {
        tag,
        onRemove: removable ? onRemove : void 0,
        onClick: onTagClick ? () => onTagClick(tag) : void 0,
        removable,
        size
      },
      tag.id
    )),
    hiddenCount > 0 && /* @__PURE__ */ jsxs(
      "span",
      {
        className: cn(
          "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-muted-foreground",
          size === "sm" && "text-xs",
          size === "md" && "text-sm",
          size === "lg" && "text-base"
        ),
        children: [
          "+",
          hiddenCount,
          " more"
        ]
      }
    )
  ] });
}
function TagInput({
  value,
  onChange,
  availableTags = [],
  placeholder = "Add tags...",
  disabled = false,
  maxTags,
  allowCreate = false,
  onCreate,
  className
}) {
  const [inputValue, setInputValue] = React13.useState("");
  const [isOpen, setIsOpen] = React13.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React13.useState(-1);
  const inputRef = React13.useRef(null);
  const containerRef = React13.useRef(null);
  const filteredTags = availableTags.filter(
    (tag) => !value.some((v) => v.id === tag.id) && tag.name.toLowerCase().includes(inputValue.toLowerCase())
  );
  const canCreate = allowCreate && inputValue.trim() && !availableTags.some(
    (t) => t.name.toLowerCase() === inputValue.toLowerCase()
  ) && !value.some((t) => t.name.toLowerCase() === inputValue.toLowerCase());
  const canAddMore = !maxTags || value.length < maxTags;
  const handleSelect = (tag) => {
    if (canAddMore) {
      onChange([...value, tag]);
      setInputValue("");
      setIsOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    }
  };
  const handleRemove = (tag) => {
    onChange(value.filter((t) => t.id !== tag.id));
  };
  const handleCreate = async () => {
    if (!canCreate || !onCreate) return;
    const newTag = await onCreate(inputValue.trim());
    handleSelect(newTag);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && !inputValue && value.length > 0) {
      const lastTag = value[value.length - 1];
      if (lastTag) handleRemove(lastTag);
      return;
    }
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
      }
      return;
    }
    const totalItems = filteredTags.length + (canCreate ? 1 : 0);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(
          (prev) => prev < totalItems - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (prev) => prev > 0 ? prev - 1 : totalItems - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredTags.length) {
            const selectedTag = filteredTags[highlightedIndex];
            if (selectedTag) handleSelect(selectedTag);
          } else if (canCreate) {
            handleCreate();
          }
        } else if (canCreate) {
          handleCreate();
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };
  React13.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative", className), children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex flex-wrap items-center gap-2 rounded-md border border-input bg-background px-3 py-2",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50"
        ),
        onClick: () => inputRef.current?.focus(),
        children: [
          /* @__PURE__ */ jsx(AnimatePresence, { mode: "popLayout", children: value.map((tag) => /* @__PURE__ */ jsx(
            TagBadge,
            {
              tag,
              size: "sm",
              removable: !disabled,
              onRemove: handleRemove
            },
            tag.id
          )) }),
          canAddMore && /* @__PURE__ */ jsx(
            "input",
            {
              ref: inputRef,
              type: "text",
              value: inputValue,
              onChange: (e) => {
                setInputValue(e.target.value);
                setIsOpen(true);
                setHighlightedIndex(-1);
              },
              onFocus: () => setIsOpen(true),
              onKeyDown: handleKeyDown,
              placeholder: value.length === 0 ? placeholder : "",
              disabled,
              className: cn(
                "flex-1 min-w-[120px] bg-transparent text-sm outline-none placeholder:text-muted-foreground",
                "disabled:cursor-not-allowed"
              )
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && (filteredTags.length > 0 || canCreate) && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -4 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -4 },
        transition: { duration: 0.15 },
        className: cn(
          "absolute left-0 right-0 top-full z-50 mt-1",
          "rounded-md border bg-popover p-1 shadow-md"
        ),
        children: /* @__PURE__ */ jsxs("div", { className: "max-h-[200px] overflow-auto", children: [
          filteredTags.map((tag, index) => /* @__PURE__ */ jsxs(
            "button",
            {
              className: cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                "focus:outline-none",
                highlightedIndex === index ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
              ),
              onClick: () => handleSelect(tag),
              onMouseEnter: () => setHighlightedIndex(index),
              children: [
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "h-3 w-3 rounded-full",
                    style: { backgroundColor: tag.color }
                  }
                ),
                /* @__PURE__ */ jsx("span", { children: tag.name })
              ]
            },
            tag.id
          )),
          canCreate && /* @__PURE__ */ jsxs(
            "button",
            {
              className: cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                "focus:outline-none",
                highlightedIndex === filteredTags.length ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
              ),
              onClick: handleCreate,
              onMouseEnter: () => setHighlightedIndex(filteredTags.length),
              children: [
                /* @__PURE__ */ jsx(Plus, { className: "h-3 w-3" }),
                /* @__PURE__ */ jsxs("span", { children: [
                  'Create "',
                  inputValue,
                  '"'
                ] })
              ]
            }
          )
        ] })
      }
    ) }),
    maxTags && /* @__PURE__ */ jsxs("div", { className: "mt-1 text-xs text-muted-foreground", children: [
      value.length,
      " / ",
      maxTags,
      " tags"
    ] })
  ] });
}
var DEFAULT_COLORS = [
  "#3b82f6",
  // Blue
  "#10b981",
  // Green
  "#f59e0b",
  // Yellow
  "#ef4444",
  // Red
  "#8b5cf6",
  // Purple
  "#ec4899",
  // Pink
  "#06b6d4",
  // Cyan
  "#f97316"
  // Orange
];
function TagColorPicker({
  value,
  onChange,
  className
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-2", className), children: DEFAULT_COLORS.map((color) => /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: () => onChange(color),
      className: cn(
        "h-6 w-6 rounded-full border-2 transition-transform",
        value === color ? "border-foreground scale-110" : "border-transparent",
        "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2"
      ),
      style: { backgroundColor: color },
      "aria-label": `Select color ${color}`,
      children: value === color && /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-white mx-auto" })
    },
    color
  )) });
}
var SidebarContext = React13.createContext(null);
function useSidebar() {
  const context = React13.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
function SidebarProvider({
  children,
  defaultCollapsed = false,
  storageKey = "sidebar-collapsed"
}) {
  const [isCollapsed, setIsCollapsedState] = React13.useState(() => {
    if (typeof window === "undefined") return defaultCollapsed;
    const stored = localStorage.getItem(storageKey);
    return stored ? stored === "true" : defaultCollapsed;
  });
  const [isMobileOpen, setIsMobileOpen] = React13.useState(false);
  const setIsCollapsed = React13.useCallback(
    (collapsed) => {
      setIsCollapsedState(collapsed);
      if (typeof window !== "undefined") {
        localStorage.setItem(storageKey, String(collapsed));
      }
    },
    [storageKey]
  );
  return /* @__PURE__ */ jsx(
    SidebarContext.Provider,
    {
      value: {
        isCollapsed,
        setIsCollapsed,
        isMobileOpen,
        setIsMobileOpen
      },
      children
    }
  );
}
function SidebarPattern() {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "absolute inset-0 pointer-events-none opacity-[0.03]",
      style: {
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
        `,
        backgroundSize: "64px 64px"
      }
    }
  );
}
function SidebarGlow() {
  return /* @__PURE__ */ jsx("div", { className: "absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-blue-500/[0.08] via-transparent to-transparent pointer-events-none" });
}
function Sidebar({ children, className }) {
  const { isCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();
  const isMobile = useIsMobile();
  React13.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileOpen, setIsMobileOpen]);
  if (isMobile) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(AnimatePresence, { children: isMobileOpen && /* @__PURE__ */ jsx(
        motion.div,
        {
          className: "fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden",
          variants: overlayVariants,
          initial: "hidden",
          animate: "visible",
          exit: "exit",
          onClick: () => setIsMobileOpen(false)
        }
      ) }),
      /* @__PURE__ */ jsx(AnimatePresence, { children: isMobileOpen && /* @__PURE__ */ jsxs(
        motion.aside,
        {
          className: cn(
            "fixed inset-y-0 left-0 z-50 w-72 lg:hidden",
            "bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900",
            "border-r border-white/[0.08] shadow-2xl shadow-black/50",
            "overflow-hidden",
            className
          ),
          initial: { x: "-100%" },
          animate: { x: 0 },
          exit: { x: "-100%" },
          transition: { type: "spring", damping: 25, stiffness: 200 },
          children: [
            /* @__PURE__ */ jsx(SidebarPattern, {}),
            /* @__PURE__ */ jsx(SidebarGlow, {}),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => setIsMobileOpen(false),
                className: cn(
                  "absolute right-3 top-4 z-10 p-2 rounded-lg",
                  "text-slate-400 hover:text-white",
                  "hover:bg-white/[0.08] transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                ),
                "aria-label": "Close sidebar",
                children: /* @__PURE__ */ jsx(X, { className: "h-5 w-5" })
              }
            ),
            /* @__PURE__ */ jsx("div", { className: "relative flex h-full flex-col", children })
          ]
        }
      ) })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    motion.aside,
    {
      className: cn(
        "hidden lg:flex lg:flex-col relative",
        "sticky top-0 h-screen",
        "bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900",
        "border-r border-white/[0.06]",
        "overflow-hidden",
        className
      ),
      animate: {
        width: isCollapsed ? 72 : 256
      },
      transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] },
      children: [
        /* @__PURE__ */ jsx(SidebarPattern, {}),
        /* @__PURE__ */ jsx(SidebarGlow, {}),
        /* @__PURE__ */ jsx("div", { className: "relative flex h-full flex-col", children })
      ]
    }
  );
}
function SidebarHeader({ children, className }) {
  const { isCollapsed } = useSidebar();
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex border-b border-white/[0.06]",
        isCollapsed ? "flex-col items-center gap-2 py-3 px-2" : "h-16 items-center justify-between px-4",
        className
      ),
      children
    }
  );
}
function SidebarContent({ children, className }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex-1 overflow-auto py-4",
        "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10",
        className
      ),
      children
    }
  );
}
function SidebarFooter({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: cn("border-t border-white/[0.06] p-4", className), children });
}
function SidebarGroup({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: cn("px-3 py-2", className), children });
}
function SidebarGroupLabel({
  children,
  className
}) {
  const { isCollapsed } = useSidebar();
  if (isCollapsed) {
    return /* @__PURE__ */ jsx("div", { className: "mx-auto my-2 h-px w-6 bg-white/[0.1]" });
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.15em]",
        "text-slate-500",
        className
      ),
      children
    }
  );
}
function SidebarItem({
  icon,
  label,
  href,
  onClick,
  active = false,
  badge,
  className,
  as: Component = href ? "a" : "button"
}) {
  const { isCollapsed, setIsMobileOpen } = useSidebar();
  const isMobile = useIsMobile();
  const handleClick = () => {
    onClick?.();
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { className: cn(
      "shrink-0 transition-colors",
      active ? "text-blue-400" : "text-slate-400 group-hover:text-white"
    ), children: icon }),
    /* @__PURE__ */ jsx(AnimatePresence, { initial: false, children: !isCollapsed && /* @__PURE__ */ jsx(
      motion.span,
      {
        className: "truncate",
        initial: { opacity: 0, width: 0 },
        animate: { opacity: 1, width: "auto" },
        exit: { opacity: 0, width: 0 },
        transition: { duration: 0.15 },
        children: label
      }
    ) }),
    badge && !isCollapsed && /* @__PURE__ */ jsx("span", { className: "ml-auto", children: badge })
  ] });
  const baseClassName = cn(
    "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium",
    "transition-all duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50",
    active ? cn(
      "bg-white/[0.08] text-white",
      "border border-white/[0.1]",
      "shadow-sm shadow-black/20",
      // Subtle inner glow for active state
      "before:absolute before:inset-0 before:rounded-lg before:bg-gradient-to-r before:from-blue-500/10 before:to-transparent before:pointer-events-none"
    ) : cn(
      "text-slate-400",
      "border border-transparent",
      "hover:bg-white/[0.05] hover:text-white",
      "hover:border-white/[0.08]"
    ),
    isCollapsed && "justify-center px-2",
    className
  );
  if (href) {
    return /* @__PURE__ */ jsx(Component, { href, className: baseClassName, onClick: handleClick, children: content });
  }
  return /* @__PURE__ */ jsx(Component, { type: "button", className: baseClassName, onClick: handleClick, children: content });
}
function SidebarToggle({ className }) {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  return /* @__PURE__ */ jsx(
    "button",
    {
      onClick: () => setIsCollapsed(!isCollapsed),
      className: cn(
        "hidden lg:flex items-center justify-center rounded-lg p-2",
        "text-slate-400 hover:text-white",
        "hover:bg-white/[0.05] border border-transparent hover:border-white/[0.08]",
        "transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50",
        className
      ),
      "aria-label": isCollapsed ? "Expand sidebar" : "Collapse sidebar",
      children: isCollapsed ? /* @__PURE__ */ jsx(PanelLeftOpen, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx(PanelLeftClose, { className: "h-5 w-5" })
    }
  );
}
function SidebarMobileTrigger({ className }) {
  const { setIsMobileOpen } = useSidebar();
  return /* @__PURE__ */ jsx(
    "button",
    {
      onClick: () => setIsMobileOpen(true),
      className: cn(
        "lg:hidden flex items-center justify-center rounded-lg p-2",
        "text-muted-foreground hover:text-foreground",
        "hover:bg-accent transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      ),
      "aria-label": "Open sidebar",
      children: /* @__PURE__ */ jsx(PanelLeftOpen, { className: "h-5 w-5" })
    }
  );
}
var typeIcons = {
  contact: /* @__PURE__ */ jsx(Users, { className: "h-4 w-4" }),
  document: /* @__PURE__ */ jsx(FileText, { className: "h-4 w-4" }),
  form: /* @__PURE__ */ jsx(ClipboardList, { className: "h-4 w-4" }),
  voicescript: /* @__PURE__ */ jsx(Phone, { className: "h-4 w-4" }),
  message: /* @__PURE__ */ jsx(MessageSquare, { className: "h-4 w-4" }),
  docs: /* @__PURE__ */ jsx(BookOpen, { className: "h-4 w-4" })
};
var typeColors = {
  contact: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  document: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  form: "text-violet-400 bg-violet-500/10 border-violet-500/20",
  voicescript: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  message: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  // Slate rather than a colour: documentation is the one category that is not the user's own
  // data, and it should read as reference material beside their records, not compete with them.
  docs: "text-slate-300 bg-slate-500/10 border-slate-400/20"
};
var dropdownVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.96,
    transition: { duration: 0.1 }
  }
};
var mobileOverlayVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 }
  }
};
var mobilePanelVariants = {
  hidden: { y: "-100%" },
  visible: {
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    y: "-100%",
    transition: { duration: 0.2 }
  }
};
var resultsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05
    }
  }
};
var resultItemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.15 }
  }
};
function GlobalSearch({
  placeholder = "Search contacts, documents, forms...",
  categories = [],
  onSearch,
  onResultClick,
  isLoading = false,
  className,
  as: LinkComponent = "a"
}) {
  const [isOpen, setIsOpen] = React13.useState(false);
  const [query, setQuery] = React13.useState("");
  const [activeIndex, setActiveIndex] = React13.useState(-1);
  const inputRef = React13.useRef(null);
  const isMobile = useIsMobile();
  React13.useEffect(() => {
    const timer = setTimeout(() => {
      if (query && onSearch) {
        onSearch(query);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, onSearch]);
  React13.useEffect(() => {
    const handleKeyDown2 = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(e.target.tagName)) {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown2);
    return () => document.removeEventListener("keydown", handleKeyDown2);
  }, []);
  React13.useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  const allResults = React13.useMemo(() => {
    return categories.flatMap((cat) => cat.results);
  }, [categories]);
  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex(
          (prev) => prev < allResults.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && allResults[activeIndex]) {
          handleResultClick(allResults[activeIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setQuery("");
        setActiveIndex(-1);
        break;
    }
  };
  const handleResultClick = (result) => {
    onResultClick?.(result);
    setIsOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };
  const handleClose = () => {
    setIsOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };
  const hasResults = categories.some((cat) => cat.results.length > 0);
  if (isMobile) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setIsOpen(true),
          className: cn(
            "flex h-10 w-10 items-center justify-center rounded-lg",
            "text-muted-foreground hover:text-foreground",
            "hover:bg-accent transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className
          ),
          "aria-label": "Open search",
          children: /* @__PURE__ */ jsx(Search, { className: "h-5 w-5" })
        }
      ),
      /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          motion.div,
          {
            className: "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
            variants: mobileOverlayVariants,
            initial: "hidden",
            animate: "visible",
            exit: "exit",
            onClick: handleClose
          }
        ),
        /* @__PURE__ */ jsxs(
          motion.div,
          {
            className: cn(
              "fixed inset-0 z-50",
              "bg-slate-900"
            ),
            variants: mobilePanelVariants,
            initial: "hidden",
            animate: "visible",
            exit: "exit",
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: "absolute inset-0 pointer-events-none opacity-[0.02]",
                  style: {
                    backgroundImage: `
                      linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: "48px 48px"
                  }
                }
              ),
              /* @__PURE__ */ jsx("div", { className: "absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-blue-500/[0.08] via-transparent to-transparent pointer-events-none" }),
              /* @__PURE__ */ jsx("div", { className: "relative border-b border-white/[0.06] p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: handleClose,
                    className: cn(
                      "flex h-10 w-10 items-center justify-center rounded-lg",
                      "text-slate-400 hover:text-white",
                      "hover:bg-white/[0.05] transition-colors"
                    ),
                    children: /* @__PURE__ */ jsx(ArrowLeft, { className: "h-5 w-5" })
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "flex-1 relative", children: [
                  /* @__PURE__ */ jsx(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" }),
                  /* @__PURE__ */ jsx(
                    "input",
                    {
                      ref: inputRef,
                      type: "text",
                      value: query,
                      onChange: (e) => setQuery(e.target.value),
                      onKeyDown: handleKeyDown,
                      placeholder,
                      className: cn(
                        "w-full h-11 pl-10 pr-4 rounded-lg",
                        "bg-white/[0.05] border border-white/[0.08]",
                        "text-white placeholder:text-slate-500",
                        "focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.08]",
                        "transition-colors"
                      )
                    }
                  ),
                  query && /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => setQuery(""),
                      className: "absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white",
                      children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
                    }
                  )
                ] })
              ] }) }),
              /* @__PURE__ */ jsx("div", { className: "relative flex-1 overflow-auto p-4", children: isLoading ? /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-12", children: /* @__PURE__ */ jsx(Loader2, { className: "h-6 w-6 text-blue-400 animate-spin" }) }) : query && hasResults ? /* @__PURE__ */ jsx(
                motion.div,
                {
                  variants: resultsContainerVariants,
                  initial: "hidden",
                  animate: "visible",
                  className: "space-y-6",
                  children: categories.map(
                    (category) => category.results.length > 0 && /* @__PURE__ */ jsx(
                      SearchCategoryGroup,
                      {
                        category,
                        activeIndex,
                        allResults,
                        onResultClick: handleResultClick,
                        LinkComponent
                      },
                      category.type
                    )
                  )
                }
              ) : query ? /* @__PURE__ */ jsxs("div", { className: "text-center py-12", children: [
                /* @__PURE__ */ jsx(Search, { className: "h-12 w-12 text-slate-600 mx-auto mb-4" }),
                /* @__PURE__ */ jsxs("p", { className: "text-slate-400", children: [
                  'No results found for "',
                  query,
                  '"'
                ] }),
                /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-600 mt-1", children: "Try a different search term" })
              ] }) : /* @__PURE__ */ jsxs("div", { className: "text-center py-12", children: [
                /* @__PURE__ */ jsx(Search, { className: "h-12 w-12 text-slate-600 mx-auto mb-4" }),
                /* @__PURE__ */ jsx("p", { className: "text-slate-400", children: "Search for anything" }),
                /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-600 mt-1", children: "Contacts, documents, forms, and more" })
              ] }) })
            ]
          }
        )
      ] }) })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("relative", className), children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-center gap-2 rounded-lg px-3 h-10 w-full max-w-[400px]",
          "bg-slate-100 dark:bg-white/[0.05]",
          "border border-slate-200 dark:border-white/[0.08]",
          "hover:bg-slate-50 dark:hover:bg-white/[0.08]",
          "hover:border-slate-300 dark:hover:border-white/[0.12]",
          "focus-within:bg-white dark:focus-within:bg-white/[0.1]",
          "focus-within:border-blue-500/50 dark:focus-within:border-blue-500/50",
          "focus-within:ring-2 focus-within:ring-blue-500/20",
          "transition-all duration-200",
          isOpen && "bg-white dark:bg-white/[0.1] border-blue-500/50 ring-2 ring-blue-500/20"
        ),
        onClick: () => setIsOpen(true),
        children: [
          /* @__PURE__ */ jsx(Search, { className: "h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" }),
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: inputRef,
              type: "text",
              value: query,
              onChange: (e) => {
                setQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              },
              onFocus: () => setIsOpen(true),
              onKeyDown: handleKeyDown,
              placeholder,
              className: cn(
                "flex-1 bg-transparent text-sm",
                "text-slate-900 dark:text-white",
                "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                "focus:outline-none"
              )
            }
          ),
          /* @__PURE__ */ jsxs(
            "kbd",
            {
              className: cn(
                "hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded",
                "text-[10px] font-medium",
                "text-slate-400 dark:text-slate-500",
                "bg-slate-200/50 dark:bg-white/[0.05]",
                "border border-slate-200 dark:border-white/[0.08]"
              ),
              children: [
                /* @__PURE__ */ jsx(Command, { className: "h-2.5 w-2.5" }),
                "K"
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "fixed inset-0 z-40",
          onClick: handleClose
        }
      ),
      /* @__PURE__ */ jsx(
        motion.div,
        {
          variants: dropdownVariants,
          initial: "hidden",
          animate: "visible",
          exit: "exit",
          className: cn(
            "absolute top-full left-0 right-0 mt-2 z-50",
            "bg-white dark:bg-slate-900",
            "border border-slate-200 dark:border-white/[0.08]",
            "rounded-xl shadow-xl dark:shadow-2xl dark:shadow-black/50",
            "max-h-[400px] overflow-y-auto",
            "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-200 dark:scrollbar-thumb-white/10"
          ),
          children: isLoading ? /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-8", children: /* @__PURE__ */ jsx(Loader2, { className: "h-5 w-5 text-blue-500 animate-spin" }) }) : query && hasResults ? /* @__PURE__ */ jsx(
            motion.div,
            {
              variants: resultsContainerVariants,
              initial: "hidden",
              animate: "visible",
              className: "p-2",
              children: categories.map(
                (category) => category.results.length > 0 && /* @__PURE__ */ jsx(
                  SearchCategoryGroup,
                  {
                    category,
                    activeIndex,
                    allResults,
                    onResultClick: handleResultClick,
                    LinkComponent
                  },
                  category.type
                )
              )
            }
          ) : query ? /* @__PURE__ */ jsxs("div", { className: "text-center py-8 px-4", children: [
            /* @__PURE__ */ jsx(Search, { className: "h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" }),
            /* @__PURE__ */ jsxs("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: [
              'No results found for "',
              query,
              '"'
            ] })
          ] }) : /* @__PURE__ */ jsxs("div", { className: "p-4", children: [
            /* @__PURE__ */ jsx("p", { className: "text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3", children: "Quick Actions" }),
            /* @__PURE__ */ jsx("div", { className: "space-y-1", children: [
              { icon: /* @__PURE__ */ jsx(Users, { className: "h-4 w-4" }), label: "Search contacts", type: "contact" },
              { icon: /* @__PURE__ */ jsx(FileText, { className: "h-4 w-4" }), label: "Find documents", type: "document" },
              { icon: /* @__PURE__ */ jsx(ClipboardList, { className: "h-4 w-4" }), label: "Browse forms", type: "form" }
            ].map((item) => /* @__PURE__ */ jsxs(
              "button",
              {
                onClick: () => setQuery(item.label.split(" ")[1] || ""),
                className: cn(
                  "flex items-center gap-3 w-full px-3 py-2 rounded-lg",
                  "text-sm text-slate-600 dark:text-slate-400",
                  "hover:bg-slate-100 dark:hover:bg-white/[0.05]",
                  "transition-colors"
                ),
                children: [
                  /* @__PURE__ */ jsx("span", { className: cn(
                    "flex h-8 w-8 items-center justify-center rounded-md",
                    typeColors[item.type]
                  ), children: item.icon }),
                  /* @__PURE__ */ jsx("span", { children: item.label }),
                  /* @__PURE__ */ jsx(ArrowRight, { className: "h-3 w-3 ml-auto text-slate-400" })
                ]
              },
              item.type
            )) })
          ] })
        }
      )
    ] }) })
  ] });
}
function SearchCategoryGroup({
  category,
  activeIndex,
  allResults,
  onResultClick,
  LinkComponent
}) {
  return /* @__PURE__ */ jsxs("div", { className: "mb-2 last:mb-0", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 px-3 py-2", children: [
      /* @__PURE__ */ jsx("span", { className: cn(
        "flex h-5 w-5 items-center justify-center rounded",
        typeColors[category.type]
      ), children: category.icon }),
      /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500", children: category.label }),
      /* @__PURE__ */ jsxs("span", { className: "text-xs text-slate-300 dark:text-slate-600", children: [
        "(",
        category.results.length,
        ")"
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-0.5", children: category.results.map((result) => {
      const globalIndex = allResults.findIndex((r) => r.id === result.id);
      const isActive = globalIndex === activeIndex;
      return /* @__PURE__ */ jsx(
        SearchResultItem,
        {
          result,
          isActive,
          onClick: () => onResultClick(result),
          LinkComponent
        },
        result.id
      );
    }) })
  ] });
}
function SearchResultItem({
  result,
  isActive,
  onClick,
  LinkComponent
}) {
  const Component = result.href ? LinkComponent : "button";
  return /* @__PURE__ */ jsx(motion.div, { variants: resultItemVariants, children: /* @__PURE__ */ jsxs(
    Component,
    {
      href: result.href,
      onClick,
      className: cn(
        "flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left",
        "transition-all duration-150",
        isActive ? cn(
          "bg-blue-50 dark:bg-white/[0.08]",
          "border border-blue-200 dark:border-white/[0.1]"
        ) : cn(
          "border border-transparent",
          "hover:bg-slate-50 dark:hover:bg-white/[0.05]"
        )
      ),
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
              "border",
              typeColors[result.type]
            ),
            children: result.icon || typeIcons[result.type]
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx("p", { className: cn(
            "text-sm font-medium truncate",
            "text-slate-900 dark:text-white"
          ), children: result.title }),
          result.subtitle && /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400 truncate", children: result.subtitle })
        ] }),
        /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded",
              "bg-slate-100 dark:bg-white/[0.05]",
              "text-slate-400 dark:text-slate-500"
            ),
            children: result.type
          }
        )
      ]
    }
  ) });
}
function TopNav({
  user,
  logo,
  title,
  actions,
  onLogout,
  onSettings,
  onProfile,
  onHelp,
  notificationCount = 0,
  onNotifications,
  className,
  // Search props
  searchEnabled = true,
  searchPlaceholder,
  searchCategories = [],
  onSearch,
  onSearchResultClick,
  isSearchLoading = false,
  searchLinkComponent
}) {
  return /* @__PURE__ */ jsxs(
    "header",
    {
      className: cn(
        "sticky top-0 z-30 flex h-16 items-center border-b bg-background px-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4 flex-1", children: [
          /* @__PURE__ */ jsx(SidebarMobileTrigger, {}),
          logo && /* @__PURE__ */ jsx("div", { className: "hidden sm:block", children: logo }),
          title && /* @__PURE__ */ jsx("h1", { className: "text-lg font-semibold truncate max-w-[150px] lg:max-w-[200px] hidden lg:block", children: title }),
          searchEnabled && /* @__PURE__ */ jsx("div", { className: "hidden md:flex flex-1 max-w-md", children: /* @__PURE__ */ jsx(
            GlobalSearch,
            {
              placeholder: searchPlaceholder,
              categories: searchCategories,
              onSearch,
              onResultClick: onSearchResultClick,
              isLoading: isSearchLoading,
              as: searchLinkComponent,
              className: "w-full"
            }
          ) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 sm:gap-2 shrink-0", children: [
          searchEnabled && /* @__PURE__ */ jsx("div", { className: "md:hidden", children: /* @__PURE__ */ jsx(
            GlobalSearch,
            {
              placeholder: searchPlaceholder,
              categories: searchCategories,
              onSearch,
              onResultClick: onSearchResultClick,
              isLoading: isSearchLoading,
              as: searchLinkComponent
            }
          ) }),
          actions,
          onNotifications && /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: onNotifications,
              className: cn(
                "relative flex h-10 w-10 items-center justify-center rounded-lg",
                "text-muted-foreground hover:bg-accent hover:text-foreground transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              ),
              "aria-label": "Notifications",
              children: [
                /* @__PURE__ */ jsx(Bell, { className: "h-5 w-5" }),
                notificationCount > 0 && /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: cn(
                      "absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center",
                      "rounded-full bg-destructive px-1 text-xs font-medium text-destructive-foreground"
                    ),
                    children: notificationCount > 99 ? "99+" : notificationCount
                  }
                )
              ]
            }
          ),
          onHelp && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onHelp,
              className: cn(
                "hidden sm:flex h-10 w-10 items-center justify-center rounded-lg",
                "text-muted-foreground hover:bg-accent hover:text-foreground transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              ),
              "aria-label": "Help",
              children: /* @__PURE__ */ jsx(HelpCircle, { className: "h-5 w-5" })
            }
          ),
          user && /* @__PURE__ */ jsx(
            UserMenu,
            {
              user,
              onLogout,
              onSettings,
              onProfile,
              onHelp
            }
          )
        ] })
      ]
    }
  );
}
function UserMenu({
  user,
  onLogout,
  onSettings,
  onProfile,
  onHelp
}) {
  const [isOpen, setIsOpen] = React13.useState(false);
  const initials = user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  return /* @__PURE__ */ jsxs(DropdownMenuPrimitive2.Root, { open: isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Trigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      "button",
      {
        className: cn(
          "flex items-center gap-2 rounded-lg p-1.5 pr-2",
          "hover:bg-accent transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        ),
        children: [
          /* @__PURE__ */ jsxs(Avatar, { className: "h-8 w-8", children: [
            user.avatar && /* @__PURE__ */ jsx(AvatarImage, { src: user.avatar, alt: user.name }),
            /* @__PURE__ */ jsx(AvatarFallback, { className: "bg-primary-600 text-white text-sm", children: initials })
          ] }),
          /* @__PURE__ */ jsx("span", { className: "hidden lg:block text-sm font-medium max-w-[120px] truncate", children: user.name }),
          /* @__PURE__ */ jsx(
            ChevronDown,
            {
              className: cn(
                "hidden lg:block h-4 w-4 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-180"
              )
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Portal, { forceMount: true, children: /* @__PURE__ */ jsx(
      DropdownMenuPrimitive2.Content,
      {
        align: "end",
        sideOffset: 8,
        asChild: true,
        children: /* @__PURE__ */ jsxs(
          motion.div,
          {
            initial: { opacity: 0, y: -8, scale: 0.96 },
            animate: { opacity: 1, y: 0, scale: 1 },
            exit: { opacity: 0, y: -8, scale: 0.96 },
            transition: { duration: 0.15 },
            className: cn(
              "z-50 min-w-[220px] rounded-xl border bg-popover p-1.5 shadow-lg",
              "focus:outline-none"
            ),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "border-b border-border px-3 py-3 mb-1.5", children: [
                /* @__PURE__ */ jsx("p", { className: "font-medium text-sm", children: user.name }),
                /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground truncate", children: user.email }),
                user.role && /* @__PURE__ */ jsx("span", { className: cn(
                  "inline-flex items-center mt-1.5 px-2 py-0.5 rounded-md text-xs font-medium",
                  "bg-primary/10 text-primary"
                ), children: user.role })
              ] }),
              onProfile && /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Item, { asChild: true, children: /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: onProfile,
                  className: cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
                    "outline-none cursor-pointer",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:bg-accent focus:text-accent-foreground",
                    "transition-colors"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(User, { className: "h-4 w-4 text-muted-foreground" }),
                    "Profile"
                  ]
                }
              ) }),
              onSettings && /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Item, { asChild: true, children: /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: onSettings,
                  className: cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
                    "outline-none cursor-pointer",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:bg-accent focus:text-accent-foreground",
                    "transition-colors"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(Settings, { className: "h-4 w-4 text-muted-foreground" }),
                    "Settings"
                  ]
                }
              ) }),
              onHelp && /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Item, { asChild: true, children: /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: onHelp,
                  className: cn(
                    "sm:hidden flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
                    "outline-none cursor-pointer",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:bg-accent focus:text-accent-foreground",
                    "transition-colors"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(HelpCircle, { className: "h-4 w-4 text-muted-foreground" }),
                    "Help"
                  ]
                }
              ) }),
              onLogout && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Separator, { className: "my-1.5 h-px bg-border" }),
                /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Item, { asChild: true, children: /* @__PURE__ */ jsxs(
                  "button",
                  {
                    onClick: onLogout,
                    className: cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-destructive",
                      "outline-none cursor-pointer",
                      "hover:bg-destructive/10",
                      "focus:bg-destructive/10",
                      "transition-colors"
                    ),
                    children: [
                      /* @__PURE__ */ jsx(LogOut, { className: "h-4 w-4" }),
                      "Log out"
                    ]
                  }
                ) })
              ] })
            ]
          }
        )
      }
    ) }) })
  ] });
}
function BottomNav({
  items = [],
  className,
  as: LinkComponent = "a",
  centerAction
}) {
  const maxItems = centerAction ? 4 : 5;
  const displayItems = items.slice(0, maxItems);
  const leftItems = centerAction ? displayItems.slice(0, 2) : displayItems;
  const rightItems = centerAction ? displayItems.slice(2, 4) : [];
  const renderNavItem = (item, index) => {
    const Component = item.href ? LinkComponent : "button";
    return /* @__PURE__ */ jsxs(
      Component,
      {
        href: item.href,
        onClick: item.onClick,
        className: cn(
          "relative flex flex-col items-center justify-center gap-1 px-3 py-2 min-w-touch",
          "transition-colors duration-200",
          item.active ? "text-primary" : "text-muted-foreground"
        ),
        children: [
          /* @__PURE__ */ jsxs("span", { className: "relative", children: [
            item.icon,
            item.badge !== void 0 && item.badge > 0 && /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center",
                  "rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground"
                ),
                children: item.badge > 99 ? "99+" : item.badge
              }
            )
          ] }),
          /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium", children: item.label }),
          item.active && /* @__PURE__ */ jsx(
            motion.div,
            {
              layoutId: "bottomNavIndicator",
              className: "absolute inset-x-2 top-0 h-0.5 rounded-full bg-primary",
              transition: { type: "spring", damping: 25, stiffness: 300 }
            }
          )
        ]
      },
      index
    );
  };
  return /* @__PURE__ */ jsx(
    "nav",
    {
      className: cn(
        "fixed inset-x-0 bottom-0 z-40 flex h-16 items-center",
        "border-t bg-background/95 backdrop-blur-lg pb-safe lg:hidden",
        centerAction ? "justify-between px-4" : "justify-around",
        className
      ),
      children: centerAction ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "flex items-center justify-around flex-1", children: leftItems.map((item, index) => renderNavItem(item, index)) }),
        /* @__PURE__ */ jsx("div", { className: "relative flex items-center justify-center w-16", children: /* @__PURE__ */ jsx("div", { className: "absolute top-0 -translate-y-1/2", children: /* @__PURE__ */ jsx(
          motion.button,
          {
            onClick: centerAction.onClick,
            className: cn(
              "flex h-14 w-14 items-center justify-center",
              "rounded-full bg-blue-600 text-white",
              "shadow-lg shadow-blue-600/25",
              "transition-colors hover:bg-blue-500",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            ),
            whileTap: { scale: 0.95 },
            "aria-label": centerAction.ariaLabel || "Action",
            children: centerAction.icon
          }
        ) }) }),
        /* @__PURE__ */ jsx("div", { className: "flex items-center justify-around flex-1", children: rightItems.map((item, index) => renderNavItem(item, index + 2)) })
      ] }) : (
        // Original layout without center action
        displayItems.map((item, index) => renderNavItem(item, index))
      )
    }
  );
}
var variantIcons = {
  default: /* @__PURE__ */ jsx(FileQuestion, { className: "h-12 w-12" }),
  search: /* @__PURE__ */ jsx(Search, { className: "h-12 w-12" }),
  inbox: /* @__PURE__ */ jsx(Inbox, { className: "h-12 w-12" }),
  contacts: /* @__PURE__ */ jsx(Users, { className: "h-12 w-12" }),
  documents: /* @__PURE__ */ jsx(FileText, { className: "h-12 w-12" }),
  messages: /* @__PURE__ */ jsx(MessageSquare, { className: "h-12 w-12" }),
  folder: /* @__PURE__ */ jsx(FolderOpen, { className: "h-12 w-12" })
};
function EmptyState({
  variant = "default",
  icon,
  title,
  description,
  action,
  secondaryAction,
  className
}) {
  const displayIcon = icon || variantIcons[variant];
  return /* @__PURE__ */ jsxs(
    motion.div,
    {
      className: cn(
        "flex flex-col items-center justify-center px-4 py-12 text-center",
        className
      ),
      variants: slideUpVariants,
      initial: "hidden",
      animate: "visible",
      children: [
        /* @__PURE__ */ jsx(
          motion.div,
          {
            className: "mb-4 text-muted-foreground/50",
            initial: { scale: 0.8, opacity: 0 },
            animate: { scale: 1, opacity: 1 },
            transition: { delay: 0.1, duration: 0.3 },
            children: displayIcon
          }
        ),
        /* @__PURE__ */ jsx(
          motion.h3,
          {
            className: "mb-2 text-lg font-semibold",
            initial: { opacity: 0, y: 8 },
            animate: { opacity: 1, y: 0 },
            transition: { delay: 0.15 },
            children: title
          }
        ),
        description && /* @__PURE__ */ jsx(
          motion.p,
          {
            className: "mb-6 max-w-sm text-sm text-muted-foreground",
            initial: { opacity: 0, y: 8 },
            animate: { opacity: 1, y: 0 },
            transition: { delay: 0.2 },
            children: description
          }
        ),
        (action || secondaryAction) && /* @__PURE__ */ jsxs(
          motion.div,
          {
            className: "flex flex-col gap-2 sm:flex-row",
            initial: { opacity: 0, y: 8 },
            animate: { opacity: 1, y: 0 },
            transition: { delay: 0.25 },
            children: [
              action && /* @__PURE__ */ jsx(Button, { onClick: action.onClick, children: action.label }),
              secondaryAction && /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: secondaryAction.onClick, children: secondaryAction.label })
            ]
          }
        )
      ]
    }
  );
}
function NoResultsState({
  searchQuery,
  onClearSearch,
  className
}) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      variant: "search",
      title: "No results found",
      description: searchQuery ? `We couldn't find anything matching "${searchQuery}". Try a different search term.` : "We couldn't find any results. Try adjusting your filters.",
      action: onClearSearch ? {
        label: "Clear search",
        onClick: onClearSearch
      } : void 0,
      className
    }
  );
}
function NoDataState({
  entityName = "items",
  onAdd,
  className
}) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      variant: "default",
      title: `No ${entityName} yet`,
      description: `Get started by creating your first ${entityName.slice(0, -1) || "item"}.`,
      action: onAdd ? {
        label: `Add ${entityName.slice(0, -1) || "item"}`,
        onClick: onAdd
      } : void 0,
      className
    }
  );
}
function InboxEmptyState({ className }) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      variant: "inbox",
      title: "Your inbox is empty",
      description: "When you receive messages, they'll appear here.",
      className
    }
  );
}
function ContactsEmptyState({
  onImport,
  onAddContact,
  className
}) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      variant: "contacts",
      title: "No contacts yet",
      description: "Add contacts to start sending messages, documents, and forms.",
      action: onAddContact ? {
        label: "Add contact",
        onClick: onAddContact
      } : void 0,
      secondaryAction: onImport ? {
        label: "Import from CSV",
        onClick: onImport
      } : void 0,
      className
    }
  );
}
function DocumentsEmptyState({
  onCreateTemplate,
  className
}) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      variant: "documents",
      title: "No documents yet",
      description: "Create document templates that can be sent for electronic signatures.",
      action: onCreateTemplate ? {
        label: "Create template",
        onClick: onCreateTemplate
      } : void 0,
      className
    }
  );
}
function Breadcrumbs({
  items,
  separator,
  showHome = true,
  homeHref = "/",
  maxItems,
  className,
  as: LinkComponent = "a"
}) {
  const isMobile = useIsMobile();
  const defaultMaxItems = isMobile ? 2 : void 0;
  const effectiveMaxItems = maxItems ?? defaultMaxItems;
  const displayItems = React13.useMemo(() => {
    const allItems = showHome ? [{ label: "Home", href: homeHref, icon: /* @__PURE__ */ jsx(Home, { className: "h-4 w-4" }) }, ...items] : [...items];
    if (!effectiveMaxItems || allItems.length <= effectiveMaxItems) {
      return allItems;
    }
    const lastItemsCount = effectiveMaxItems - 1;
    const firstItem = allItems[0];
    if (!firstItem) return allItems;
    return [
      firstItem,
      "ellipsis",
      ...allItems.slice(-lastItemsCount)
    ];
  }, [items, showHome, homeHref, effectiveMaxItems]);
  const SeparatorIcon = separator || /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" });
  return /* @__PURE__ */ jsx("nav", { "aria-label": "Breadcrumb", className: cn("flex items-center", className), children: /* @__PURE__ */ jsx("ol", { className: "flex items-center gap-1 text-sm", children: displayItems.map((item, index) => {
    const isLast = index === displayItems.length - 1;
    const isEllipsis = item === "ellipsis";
    return /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-1", children: [
      index > 0 && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", "aria-hidden": "true", children: SeparatorIcon }),
      isEllipsis ? /* @__PURE__ */ jsx("span", { className: "px-1 text-muted-foreground", children: /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" }) }) : isLast ? /* @__PURE__ */ jsx(BreadcrumbPage, { item }) : item.href ? /* @__PURE__ */ jsx(BreadcrumbLink, { item, LinkComponent }) : /* @__PURE__ */ jsx(BreadcrumbText, { item })
    ] }, index);
  }) }) });
}
function BreadcrumbPage({ item }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: "flex items-center gap-1.5 font-medium text-foreground",
      "aria-current": "page",
      children: [
        item.icon,
        /* @__PURE__ */ jsx("span", { className: "truncate max-w-[200px]", children: item.label })
      ]
    }
  );
}
function BreadcrumbLink({ item, LinkComponent }) {
  return /* @__PURE__ */ jsxs(
    LinkComponent,
    {
      href: item.href,
      className: cn(
        "flex items-center gap-1.5 text-muted-foreground transition-colors",
        "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
      ),
      children: [
        item.icon,
        /* @__PURE__ */ jsx("span", { className: "truncate max-w-[150px]", children: item.label })
      ]
    }
  );
}
function BreadcrumbText({ item }) {
  return /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5 text-muted-foreground", children: [
    item.icon,
    /* @__PURE__ */ jsx("span", { className: "truncate max-w-[150px]", children: item.label })
  ] });
}
function MobileBreadcrumb({
  backHref,
  backLabel = "Back",
  currentPage,
  onBack,
  className,
  as: LinkComponent = "a"
}) {
  const BackComponent = backHref ? LinkComponent : "button";
  return /* @__PURE__ */ jsxs("nav", { "aria-label": "Breadcrumb", className: cn("flex items-center gap-2", className), children: [
    /* @__PURE__ */ jsxs(
      BackComponent,
      {
        href: backHref,
        onClick: onBack,
        className: cn(
          "flex items-center gap-1 text-sm text-muted-foreground",
          "hover:text-foreground transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        ),
        children: [
          /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4 rotate-180" }),
          /* @__PURE__ */ jsx("span", { children: backLabel })
        ]
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "/" }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-medium truncate", children: currentPage })
  ] });
}
var sheetSlideVariants = {
  left: {
    hidden: { x: "-100%" },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300
      }
    },
    exit: {
      x: "-100%",
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  },
  right: {
    hidden: { x: "100%" },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300
      }
    },
    exit: {
      x: "100%",
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  },
  bottom: {
    hidden: { y: "100%" },
    visible: {
      y: 0,
      transition: {
        type: "spring",
        damping: 32,
        stiffness: 320
      }
    },
    exit: {
      y: "100%",
      transition: {
        duration: 0.22,
        ease: "easeIn"
      }
    }
  }
};
var mobileSheetVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      damping: 30,
      stiffness: 300
    }
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};
var Sheet = DialogPrimitive.Root;
var SheetTrigger = DialogPrimitive.Trigger;
var SheetClose = DialogPrimitive.Close;
var SheetPortal = DialogPrimitive.Portal;
var SheetOverlay = React13.forwardRef(({ className }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Overlay, { ref, asChild: true, children: /* @__PURE__ */ jsx(
  motion.div,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm",
      className
    ),
    variants: overlayVariants,
    initial: "hidden",
    animate: "visible",
    exit: "exit"
  }
) }));
SheetOverlay.displayName = "SheetOverlay";
var SheetContent = React13.forwardRef(
  ({
    className,
    children,
    side = "right",
    width = 600,
    maxHeight = "85vh",
    showCloseButton = true,
    preventClose = false,
    ...props
  }, ref) => {
    const isMobile = useIsMobile();
    const widthStyle = typeof width === "number" ? `${width}px` : width;
    const maxHeightStyle = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
    const sidePositionClasses = {
      left: "left-0",
      right: "right-0",
      bottom: "bottom-0"
    };
    const renderBottomSheet = () => /* @__PURE__ */ jsxs(
      motion.div,
      {
        className: cn(
          "fixed inset-x-0 bottom-0 z-50 bg-background",
          "border-t border-border shadow-[0_-12px_40px_rgba(15,23,42,0.18)]",
          "rounded-t-2xl flex flex-col overflow-hidden",
          className
        ),
        style: { maxHeight: maxHeightStyle },
        variants: sheetSlideVariants.bottom,
        initial: "hidden",
        animate: "visible",
        exit: "exit",
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsxs(
            DialogPrimitive.Close,
            {
              className: "absolute right-4 top-4 rounded-full p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "aria-label": "Close",
              children: [
                /* @__PURE__ */ jsx(X, { className: "h-5 w-5" }),
                /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    );
    return /* @__PURE__ */ jsx(SheetPortal, { children: /* @__PURE__ */ jsxs(AnimatePresence, { mode: "wait", children: [
      /* @__PURE__ */ jsx(SheetOverlay, {}),
      /* @__PURE__ */ jsx(
        DialogPrimitive.Content,
        {
          ref,
          onPointerDownOutside: preventClose ? (e) => e.preventDefault() : void 0,
          onEscapeKeyDown: preventClose ? (e) => e.preventDefault() : void 0,
          asChild: true,
          ...props,
          children: side === "bottom" ? renderBottomSheet() : isMobile ? (
            // Mobile: Full-screen slide from right (for left/right sides)
            /* @__PURE__ */ jsxs(
              motion.div,
              {
                className: cn(
                  "fixed inset-0 z-50 bg-background",
                  "flex flex-col overflow-hidden",
                  className
                ),
                variants: mobileSheetVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit",
                children: [
                  children,
                  showCloseButton && /* @__PURE__ */ jsxs(
                    DialogPrimitive.Close,
                    {
                      className: "absolute right-4 top-4 rounded-full p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                      "aria-label": "Close",
                      children: [
                        /* @__PURE__ */ jsx(X, { className: "h-5 w-5" }),
                        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
                      ]
                    }
                  )
                ]
              }
            )
          ) : (
            // Desktop: Slide-over panel
            /* @__PURE__ */ jsxs(
              motion.div,
              {
                className: cn(
                  "fixed inset-y-0 z-50 bg-background border-l border-border shadow-xl",
                  "flex flex-col overflow-hidden",
                  sidePositionClasses[side],
                  className
                ),
                style: { width: widthStyle, maxWidth: "100vw" },
                variants: sheetSlideVariants[side],
                initial: "hidden",
                animate: "visible",
                exit: "exit",
                children: [
                  children,
                  showCloseButton && /* @__PURE__ */ jsxs(
                    DialogPrimitive.Close,
                    {
                      className: "absolute right-4 top-4 rounded-full p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                      "aria-label": "Close",
                      children: [
                        /* @__PURE__ */ jsx(X, { className: "h-5 w-5" }),
                        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
                      ]
                    }
                  )
                ]
              }
            )
          )
        }
      )
    ] }) });
  }
);
SheetContent.displayName = "SheetContent";
var SheetHeader = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 p-6 pb-4 border-b border-border",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse gap-2 p-6 pt-4 border-t border-border sm:flex-row sm:justify-end sm:gap-3",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold text-foreground", className),
    ...props
  }
));
SheetTitle.displayName = "SheetTitle";
var SheetDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = "SheetDescription";
var SheetBody = ({ className, ...props }) => /* @__PURE__ */ jsx("div", { className: cn("flex-1 overflow-y-auto p-6", className), ...props });
SheetBody.displayName = "SheetBody";
var Checkbox = React13.forwardRef(({ className, label, description, error, id, ...props }, ref) => {
  const generatedId = React13.useId();
  const checkboxId = id || generatedId;
  const checkbox = /* @__PURE__ */ jsx(
    CheckboxPrimitive.Root,
    {
      ref,
      id: checkboxId,
      className: cn(
        // Base styles
        "peer h-5 w-5 shrink-0 rounded-md border-2 border-slate-300",
        "bg-white",
        // Focus styles
        "focus-visible:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500/20",
        // Checked styles - blue accent
        "data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-500 data-[state=checked]:text-white",
        // Indeterminate styles
        "data-[state=indeterminate]:border-blue-500 data-[state=indeterminate]:bg-blue-500 data-[state=indeterminate]:text-white",
        // Disabled styles
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
        // Dark mode
        "dark:border-slate-600 dark:bg-slate-900",
        "dark:focus-visible:border-blue-500 dark:focus-visible:ring-blue-500/20",
        "dark:data-[state=checked]:border-blue-500 dark:data-[state=checked]:bg-blue-500",
        "dark:disabled:bg-slate-800",
        // Transition
        "transition-all duration-200",
        // Error state
        error && "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500/20",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        CheckboxPrimitive.Indicator,
        {
          className: cn("flex items-center justify-center text-current"),
          children: props.checked === "indeterminate" ? /* @__PURE__ */ jsx(Minus, { className: "h-3 w-3", strokeWidth: 3 }) : /* @__PURE__ */ jsx(Check, { className: "h-3 w-3", strokeWidth: 3 })
        }
      )
    }
  );
  if (!label && !description) {
    return checkbox;
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
    checkbox,
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: checkboxId,
          className: cn(
            "text-sm font-medium leading-none cursor-pointer select-none",
            "text-slate-700 dark:text-slate-300",
            "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            error && "text-red-500 dark:text-red-400"
          ),
          children: label
        }
      ),
      description && /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: description }),
      error && /* @__PURE__ */ jsx("p", { className: "text-sm text-red-500 dark:text-red-400", children: error })
    ] })
  ] });
});
Checkbox.displayName = "Checkbox";
var RadioGroup = React13.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      className: cn("grid gap-3", className),
      ...props,
      ref
    }
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
var RadioGroupItem = React13.forwardRef(({ className, label, description, id, ...props }, ref) => {
  const generatedId = React13.useId();
  const radioId = id || generatedId;
  const radio = /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      id: radioId,
      className: cn(
        // Base styles
        "aspect-square h-5 w-5 shrink-0 rounded-full border-2 border-slate-300",
        "bg-white",
        // Focus styles
        "focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500/20",
        // Checked state - blue accent
        "data-[state=checked]:border-blue-500",
        // Dark mode
        "dark:border-slate-600 dark:bg-slate-900",
        "dark:focus-visible:border-blue-500 dark:focus-visible:ring-blue-500/20",
        "dark:data-[state=checked]:border-blue-500",
        // Disabled state
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
        "dark:disabled:bg-slate-800",
        // Transition
        "transition-all duration-200",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx(Circle, { className: "h-2.5 w-2.5 fill-blue-500 text-blue-500" }) })
    }
  );
  if (!label && !description) {
    return radio;
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
    radio,
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: radioId,
          className: cn(
            "text-sm font-medium leading-none cursor-pointer select-none",
            "text-slate-700 dark:text-slate-300",
            "peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          ),
          children: label
        }
      ),
      description && /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: description })
    ] })
  ] });
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
var RadioCard = React13.forwardRef(({ className, label, description, icon, id, ...props }, ref) => {
  const generatedId = React13.useId();
  const radioId = id || generatedId;
  return /* @__PURE__ */ jsxs(
    RadioGroupPrimitive.Item,
    {
      ref,
      id: radioId,
      className: cn(
        // Base styles
        "relative flex cursor-pointer flex-col items-center justify-center",
        "rounded-xl border-2 border-slate-200 bg-white p-4 text-center",
        // Focus styles
        "focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500/20",
        // Hover state
        "hover:border-slate-300 hover:bg-slate-50",
        // Checked state
        "data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-50",
        // Dark mode
        "dark:border-slate-700 dark:bg-slate-900",
        "dark:hover:border-slate-600 dark:hover:bg-slate-800",
        "dark:data-[state=checked]:border-blue-500 dark:data-[state=checked]:bg-blue-500/10",
        // Disabled state
        "disabled:cursor-not-allowed disabled:opacity-50",
        "disabled:hover:border-slate-200 disabled:hover:bg-white",
        "dark:disabled:hover:border-slate-700 dark:disabled:hover:bg-slate-900",
        // Transition
        "transition-all duration-200",
        className
      ),
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx("div", { className: "mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400", children: icon }),
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold text-slate-900 dark:text-white", children: label }),
        description && /* @__PURE__ */ jsx("span", { className: "mt-1 text-xs text-slate-500 dark:text-slate-400", children: description }),
        /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "sr-only" })
      ]
    }
  );
});
RadioCard.displayName = "RadioCard";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React13.forwardRef(({ className, children, error, leftIcon, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      // Base styles matching Input
      "flex h-11 w-full items-center justify-between gap-2 rounded-lg",
      "border border-slate-200 bg-white",
      "px-3 py-2 text-sm text-slate-900",
      "placeholder:text-slate-400",
      // Focus styles matching auth pages
      "focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
      // Disabled styles
      "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
      // Transition
      "transition-all duration-200",
      // Dark mode
      "dark:border-slate-700 dark:bg-slate-900 dark:text-white",
      "dark:focus:border-blue-500 dark:focus:ring-blue-500/20",
      "dark:disabled:bg-slate-800",
      // Line clamp
      "[&>span]:line-clamp-1",
      // Error state
      error && "border-red-500 focus:border-red-500 focus:ring-red-500/20",
      className
    ),
    ...props,
    children: [
      leftIcon && /* @__PURE__ */ jsx("span", { className: "shrink-0 text-slate-400", children: leftIcon }),
      children,
      /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 text-slate-400" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React13.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      // Base styles
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden",
      "rounded-xl border border-slate-200 bg-white shadow-lg",
      "dark:border-slate-700 dark:bg-slate-900",
      // Animations
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      position === "popper" && "max-w-[var(--radix-select-content-available-width)]",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1.5",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Label,
  {
    ref,
    className: cn(
      "py-1.5 pl-8 pr-2 text-xs font-semibold uppercase tracking-wide",
      "text-slate-500 dark:text-slate-400",
      className
    ),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      // Base styles
      "relative flex w-full cursor-pointer select-none items-center",
      "rounded-lg py-2 pl-8 pr-2 text-sm text-slate-700",
      "whitespace-normal break-words",
      "outline-none transition-colors duration-150",
      // Hover and focus
      "hover:bg-slate-100 hover:text-slate-900",
      "focus:bg-slate-100 focus:text-slate-900",
      // Dark mode
      "dark:text-slate-300",
      "dark:hover:bg-slate-800 dark:hover:text-white",
      "dark:focus:bg-slate-800 dark:focus:text-white",
      // Disabled
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-blue-500" }) }) }),
      /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-slate-200 dark:bg-slate-700", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  isLoading = false,
  disabled = false,
  className
}) {
  const [open, setOpen] = React13.useState(false);
  const [search, setSearch] = React13.useState("");
  const inputRef = React13.useRef(null);
  const selectedOption = options.find((opt) => opt.value === value);
  const filteredOptions = React13.useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(searchLower));
  }, [options, search]);
  const handleSelect = (optionValue) => {
    onValueChange(optionValue === value ? null : optionValue);
    setOpen(false);
    setSearch("");
  };
  const handleClear = (e) => {
    e.stopPropagation();
    onValueChange(null);
  };
  React13.useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);
  return /* @__PURE__ */ jsxs(Popover.Root, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(Popover.Trigger, { asChild: true, disabled, children: /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        role: "combobox",
        "aria-expanded": open,
        className: cn(
          "flex h-11 w-full items-center justify-between gap-2 rounded-lg",
          "border border-slate-200 bg-white",
          "px-3 py-2 text-sm text-slate-900",
          "focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
          "transition-all duration-200",
          "dark:border-slate-700 dark:bg-slate-900 dark:text-white",
          "dark:focus:border-blue-500 dark:focus:ring-blue-500/20",
          "dark:disabled:bg-slate-800",
          className
        ),
        children: [
          /* @__PURE__ */ jsx("span", { className: cn("truncate", !selectedOption && "text-slate-400"), children: selectedOption ? selectedOption.label : placeholder }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 shrink-0", children: [
            value && !disabled && /* @__PURE__ */ jsx(
              "span",
              {
                role: "button",
                onClick: handleClear,
                className: "p-0.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors",
                children: /* @__PURE__ */ jsx(X, { className: "h-3.5 w-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" })
              }
            ),
            /* @__PURE__ */ jsx(
              ChevronDown,
              {
                className: cn(
                  "h-4 w-4 text-slate-400 transition-transform duration-200",
                  open && "rotate-180"
                )
              }
            )
          ] })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(Popover.Portal, { children: /* @__PURE__ */ jsxs(
      Popover.Content,
      {
        className: cn(
          "z-50 w-[var(--radix-popover-trigger-width)] min-w-[200px]",
          "rounded-xl border border-slate-200 bg-white shadow-lg",
          "dark:border-slate-700 dark:bg-slate-900",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
        ),
        sideOffset: 4,
        align: "start",
        children: [
          /* @__PURE__ */ jsx("div", { className: "p-2 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(Search, { className: "absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                ref: inputRef,
                type: "text",
                value: search,
                onChange: (e) => setSearch(e.target.value),
                placeholder: searchPlaceholder,
                className: cn(
                  "w-full h-9 rounded-lg pl-8 pr-3 text-sm",
                  "bg-slate-50 dark:bg-slate-800",
                  "border border-slate-200 dark:border-slate-700",
                  "text-slate-900 dark:text-white",
                  "placeholder:text-slate-400",
                  "focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20",
                  "transition-all duration-150"
                )
              }
            ),
            search && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setSearch(""),
                className: "absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300",
                children: /* @__PURE__ */ jsx(X, { className: "h-3.5 w-3.5" })
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx("div", { className: "max-h-60 overflow-y-auto p-1.5", children: isLoading ? /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center py-6 text-sm text-slate-500", children: [
            /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin mr-2" }),
            "Loading..."
          ] }) : filteredOptions.length === 0 ? /* @__PURE__ */ jsx("div", { className: "py-6 text-center text-sm text-slate-500", children: emptyMessage }) : filteredOptions.map((option) => {
            const isSelected = option.value === value;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => handleSelect(option.value),
                className: cn(
                  "relative flex w-full items-center rounded-lg py-2 pl-8 pr-2",
                  "text-sm text-slate-700 dark:text-slate-300",
                  "outline-none transition-colors duration-150",
                  "hover:bg-slate-100 dark:hover:bg-slate-800",
                  isSelected && "bg-slate-100 dark:bg-slate-800"
                ),
                children: [
                  /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-4 w-4 items-center justify-center", children: isSelected && /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-blue-500" }) }),
                  /* @__PURE__ */ jsx("span", { className: "truncate", children: option.label })
                ]
              },
              option.value
            );
          }) })
        ]
      }
    ) })
  ] });
}
var ALL_PREFIX = "__all__:";
function isCountry(j) {
  return !j.parentCode || j.parentCode === "GLOBAL";
}
function buildPickerData(jurisdictions) {
  const countries = [];
  const subsByCountry = /* @__PURE__ */ new Map();
  const byCode = /* @__PURE__ */ new Map();
  for (const j of jurisdictions) {
    byCode.set(j.code, j);
  }
  for (const j of jurisdictions) {
    if (isCountry(j)) {
      countries.push(j);
    } else if (j.parentCode) {
      const list = subsByCountry.get(j.parentCode) ?? [];
      list.push(j);
      subsByCountry.set(j.parentCode, list);
    }
  }
  for (const list of subsByCountry.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return { countries, subsByCountry, byCode };
}
function splitSelection(code, byCode) {
  if (!code) return { countryCode: "", subCode: "" };
  const j = byCode.get(code);
  if (!j) return { countryCode: code, subCode: "" };
  if (isCountry(j)) return { countryCode: code, subCode: "" };
  return { countryCode: j.parentCode ?? "", subCode: code };
}
function buildCountryOptions(countries, homeCountryCode, recentCodes, byCode) {
  const seen = /* @__PURE__ */ new Set();
  const ordered = [];
  if (homeCountryCode) {
    const home = countries.find((c) => c.code === homeCountryCode);
    if (home) {
      ordered.push(home);
      seen.add(home.code);
    }
  }
  if (recentCodes) {
    for (const rc of recentCodes) {
      const j = byCode.get(rc);
      if (!j) continue;
      const country = isCountry(j) ? j : j.parentCode ? byCode.get(j.parentCode) : void 0;
      if (country && !seen.has(country.code)) {
        ordered.push(country);
        seen.add(country.code);
      }
    }
  }
  const remaining = countries.filter((c) => !seen.has(c.code)).sort((a, b) => a.name.localeCompare(b.name));
  ordered.push(...remaining);
  return ordered.map((c) => ({ value: c.code, label: c.name }));
}
function JurisdictionPicker({
  jurisdictions,
  value,
  onChange,
  homeCountryCode,
  recentCodes,
  className,
  disabled,
  isLoading
}) {
  const { countries, subsByCountry, byCode } = React13.useMemo(
    () => buildPickerData(jurisdictions),
    [jurisdictions]
  );
  const { countryCode, subCode } = React13.useMemo(
    () => splitSelection(value ?? "", byCode),
    [value, byCode]
  );
  const countryOptions = React13.useMemo(
    () => buildCountryOptions(countries, homeCountryCode, recentCodes, byCode),
    [countries, homeCountryCode, recentCodes, byCode]
  );
  const subs = React13.useMemo(
    () => countryCode ? subsByCountry.get(countryCode) ?? [] : [],
    [countryCode, subsByCountry]
  );
  const subOptions = React13.useMemo(() => {
    if (!countryCode || subs.length === 0) return [];
    const country = byCode.get(countryCode);
    const allLabel = country ? `All of ${country.name}` : "All";
    return [
      { value: `${ALL_PREFIX}${countryCode}`, label: allLabel },
      ...subs.map((s) => ({ value: s.code, label: s.name }))
    ];
  }, [countryCode, subs, byCode]);
  const handleCountryChange = (next) => {
    if (!next) {
      onChange("");
      return;
    }
    if (next === countryCode) return;
    onChange(next);
  };
  const handleSubChange = (next) => {
    if (!next) return;
    if (next.startsWith(ALL_PREFIX)) {
      onChange(next.slice(ALL_PREFIX.length));
      return;
    }
    onChange(next);
  };
  const subValue = subCode || (countryCode ? `${ALL_PREFIX}${countryCode}` : "");
  const showSubPill = Boolean(countryCode) && subs.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: `flex min-w-0 items-center gap-2 ${className ?? ""}`, children: [
    /* @__PURE__ */ jsx(
      SearchableSelect,
      {
        className: "flex-1 min-w-0",
        value: countryCode,
        onValueChange: handleCountryChange,
        options: countryOptions,
        placeholder: "Select country",
        searchPlaceholder: "Search countries...",
        emptyMessage: "No countries found",
        disabled,
        isLoading
      }
    ),
    showSubPill && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4 shrink-0 text-muted-foreground", "aria-hidden": "true" }),
      /* @__PURE__ */ jsx(
        SearchableSelect,
        {
          className: "flex-1 min-w-0",
          value: subValue,
          onValueChange: handleSubChange,
          options: subOptions,
          placeholder: "State / Province",
          searchPlaceholder: "Search...",
          emptyMessage: "No sub-jurisdictions",
          disabled
        }
      )
    ] })
  ] });
}
var CHECK_ICONS = {
  CHK_INTERNAL_DNC_CLEAR: {
    Icon: Shield,
    iconColor: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-rose-100 dark:bg-rose-900/30"
  },
  CHK_NATIONAL_REGISTRY_SCRUBBED: {
    Icon: Database,
    iconColor: "text-cyan-600 dark:text-cyan-400",
    iconBg: "bg-cyan-100 dark:bg-cyan-900/30"
  },
  CHK_NATIONAL_REGISTRY_CLEAR: {
    Icon: ShieldBan,
    iconColor: "text-cyan-600 dark:text-cyan-400",
    iconBg: "bg-cyan-100 dark:bg-cyan-900/30"
  },
  CHK_WITHIN_CALLING_HOURS: {
    Icon: Calendar,
    iconColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100 dark:bg-amber-900/30"
  },
  CHK_FREQUENCY_CAP_24H_OK: {
    Icon: Repeat,
    iconColor: "text-orange-600 dark:text-orange-400",
    iconBg: "bg-orange-100 dark:bg-orange-900/30"
  },
  CHK_CONSENT_AT_LEAST: {
    Icon: ShieldCheck,
    iconColor: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/30"
  },
  CHK_CONTACT_ELIGIBLE: {
    Icon: UserCheck,
    iconColor: "text-teal-600 dark:text-teal-400",
    iconBg: "bg-teal-100 dark:bg-teal-900/30"
  },
  CHK_SENDER_ID_AND_UNSUB_PRESENT: {
    Icon: Mail,
    iconColor: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-100 dark:bg-blue-900/30"
  },
  CHK_REVOCATION_HANDLING_ENABLED: {
    Icon: Ban,
    iconColor: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-rose-100 dark:bg-rose-900/30"
  },
  CHK_WIRELESS_MARKETING_CONSENT_OK: {
    Icon: Smartphone,
    iconColor: "text-violet-600 dark:text-violet-400",
    iconBg: "bg-violet-100 dark:bg-violet-900/30"
  },
  CHK_AUTOMATION_ALLOWED_FOR_SEND: {
    Icon: Bot,
    iconColor: "text-indigo-600 dark:text-indigo-400",
    iconBg: "bg-indigo-100 dark:bg-indigo-900/30"
  }
};
var FALLBACK_OPT_IN = {
  Icon: Shield,
  iconColor: "text-slate-600 dark:text-slate-300",
  iconBg: "bg-slate-100 dark:bg-slate-800"
};
var FALLBACK_SENDING = {
  Icon: Clock,
  iconColor: "text-slate-600 dark:text-slate-300",
  iconBg: "bg-slate-100 dark:bg-slate-800"
};
var FALLBACK_OPT_OUT = {
  Icon: Shield,
  iconColor: "text-slate-600 dark:text-slate-300",
  iconBg: "bg-slate-100 dark:bg-slate-800"
};
function ruleFlowIconFor(checkId, bucket = "OPT_IN") {
  if (checkId && CHECK_ICONS[checkId]) return CHECK_ICONS[checkId];
  if (bucket === "SENDING") return FALLBACK_SENDING;
  if (bucket === "OPT_OUT") return FALLBACK_OPT_OUT;
  return FALLBACK_OPT_IN;
}
var EFFECT_RAIL = {
  BLOCK: "bg-rose-400 dark:bg-rose-500",
  CONDITION: "bg-amber-400 dark:bg-amber-500",
  ALLOW: "bg-emerald-400 dark:bg-emerald-500"
};
function RuleFlowCard({
  checkId,
  bucket,
  name,
  title,
  citation,
  detail,
  effect,
  inheritedFrom,
  subPillLabel,
  actions,
  defaultExpanded = false,
  className
}) {
  const [expanded, setExpanded] = React13.useState(defaultExpanded);
  const { Icon: Icon2, iconColor, iconBg } = ruleFlowIconFor(checkId, bucket);
  const railColor = effect ? EFFECT_RAIL[effect] : "bg-slate-300 dark:bg-slate-700";
  const heading = name?.trim() || title;
  const subtitle = name?.trim() ? title : null;
  const hasMore = Boolean(subtitle || citation || detail);
  const sourcePill = formatSourcePill(inheritedFrom ?? subPillLabel ?? null);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "group relative rounded-xl border border-slate-200/80 dark:border-slate-700/60",
        "bg-white dark:bg-slate-900 shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600",
        "transition-all",
        inheritedFrom ? "opacity-95" : "",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: cn("absolute left-0 top-0 bottom-0 w-1 rounded-l-xl", railColor) }),
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: () => hasMore && setExpanded((v) => !v),
            className: cn(
              "w-full text-left flex items-center gap-3 p-3 pl-4",
              hasMore ? "cursor-pointer" : "cursor-default"
            ),
            "aria-expanded": expanded,
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
                    iconBg
                  ),
                  children: /* @__PURE__ */ jsx(Icon2, { className: cn("w-4 h-4", iconColor) })
                }
              ),
              /* @__PURE__ */ jsx("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug truncate", children: heading }) }),
              sourcePill && /* @__PURE__ */ jsx(
                "span",
                {
                  className: "inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 tabular-nums flex-shrink-0",
                  title: inheritedFrom ? `Inherited from ${inheritedFrom}` : void 0,
                  children: sourcePill
                }
              ),
              actions && /* @__PURE__ */ jsx(
                "div",
                {
                  className: "flex items-center gap-1 flex-shrink-0",
                  onClick: (e) => e.stopPropagation(),
                  role: "presentation",
                  children: actions
                }
              ),
              hasMore && /* @__PURE__ */ jsx(
                ChevronDown,
                {
                  className: cn(
                    "w-4 h-4 text-slate-400 flex-shrink-0 transition-transform",
                    expanded ? "rotate-180" : ""
                  )
                }
              )
            ]
          }
        ),
        expanded && hasMore && /* @__PURE__ */ jsxs("div", { className: "px-4 pb-3 pl-16 space-y-1.5", children: [
          subtitle && /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-700 dark:text-slate-300 leading-relaxed", children: subtitle }),
          detail && detail !== subtitle && /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-600 dark:text-slate-400 leading-relaxed", children: detail }),
          citation && /* @__PURE__ */ jsx("p", { className: "text-[11px] text-slate-500 dark:text-slate-500 italic", children: citation })
        ] })
      ]
    }
  );
}
function formatSourcePill(code) {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  if (!upper) return null;
  if (upper === "GLOBAL") return "GL";
  if (upper === "US-FED" || upper === "US_FED" || upper === "USFED") return "US";
  const dashIdx = upper.indexOf("-");
  if (dashIdx > 0 && dashIdx < upper.length - 1) {
    return upper.slice(dashIdx + 1);
  }
  return upper;
}
function RuleArrow({ className }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("flex items-center justify-center py-0.5", className),
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx(ChevronDown, { className: "w-4 h-4 text-slate-300 dark:text-slate-600", strokeWidth: 2.5 })
    }
  );
}
function AllowsToSendPill({
  className,
  label = "ALLOWS TO SEND"
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center py-2", className), children: /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-semibold shadow-sm", children: [
    /* @__PURE__ */ jsx(Send, { className: "w-3.5 h-3.5" }),
    label,
    /* @__PURE__ */ jsx(ArrowRight, { className: "w-3.5 h-3.5" })
  ] }) });
}
function CollapsibleRuleSection({
  label,
  count: count2,
  icon: Icon2 = Check,
  defaultOpen = true,
  className,
  children
}) {
  const [open, setOpen] = React13.useState(defaultOpen);
  return (
    // pt-3 reserves space for the pill that straddles the top border
    /* @__PURE__ */ jsxs("div", { className: cn("relative pt-3", className), children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => setOpen((v) => !v),
          "aria-expanded": open,
          "aria-label": `${label} (${count2})`,
          className: cn(
            "absolute left-3 top-0 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full",
            "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-semibold shadow-sm",
            "hover:from-emerald-600 hover:to-emerald-700 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-1"
          ),
          children: [
            /* @__PURE__ */ jsx(Icon2, { className: "w-3.5 h-3.5" }),
            label,
            /* @__PURE__ */ jsxs("span", { className: "opacity-90 tabular-nums", children: [
              "(",
              count2,
              ")"
            ] })
          ]
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "rounded-xl border border-slate-200 dark:border-slate-700/60",
            "bg-slate-50/70 dark:bg-slate-800/30",
            "transition-colors"
          ),
          children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setOpen((v) => !v),
                "aria-expanded": open,
                "aria-label": `Toggle ${label}`,
                className: cn(
                  "w-full flex items-center justify-end gap-2 px-3 pt-3 pb-1",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-1 rounded-t-xl"
                ),
                children: /* @__PURE__ */ jsx(
                  ChevronDown,
                  {
                    className: cn(
                      "w-4 h-4 text-slate-400 transition-transform duration-200",
                      open ? "rotate-180" : ""
                    )
                  }
                )
              }
            ),
            open && /* @__PURE__ */ jsx("div", { className: "px-3 pb-3 pt-1", children })
          ]
        }
      )
    ] })
  );
}
function RuleFlowSectionHeader({
  label,
  count: count2,
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2 mb-2", className), children: [
    /* @__PURE__ */ jsx(ChevronRight, { className: "w-3 h-3 text-slate-400" }),
    /* @__PURE__ */ jsxs("p", { className: "text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold", children: [
      label,
      typeof count2 === "number" && /* @__PURE__ */ jsxs("span", { className: "ml-1.5 text-slate-400 dark:text-slate-500 normal-case font-normal tabular-nums", children: [
        "(",
        count2,
        ")"
      ] })
    ] })
  ] });
}
var DropdownMenu = DropdownMenuPrimitive2.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive2.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive2.Group;
var DropdownMenuPortal = DropdownMenuPrimitive2.Portal;
var DropdownMenuSub = DropdownMenuPrimitive2.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive2.RadioGroup;
var DropdownMenuSubTrigger = React13.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent",
      inset && "pl-8",
      className
    ),
    ...props,
    children
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive2.SubTrigger.displayName;
var DropdownMenuSubContent = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive2.SubContent.displayName;
var DropdownMenuContent = React13.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive2.Portal, { children: /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-xl border border-slate-200 bg-white p-1 text-slate-950 shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-50",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive2.Content.displayName;
var DropdownMenuItem = React13.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-pointer select-none items-center rounded-lg px-2 py-1.5 text-sm outline-none transition-colors focus:bg-slate-100 focus:text-slate-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-slate-700 dark:focus:text-slate-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive2.Item.displayName;
var DropdownMenuCheckboxItem = React13.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive2.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive2.ItemIndicator, { children: /* @__PURE__ */ jsx(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: /* @__PURE__ */ jsx("polyline", { points: "20 6 9 17 4 12" })
        }
      ) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive2.CheckboxItem.displayName;
var DropdownMenuRadioItem = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive2.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive2.ItemIndicator, { children: /* @__PURE__ */ jsx(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "8",
          height: "8",
          viewBox: "0 0 8 8",
          children: /* @__PURE__ */ jsx("circle", { cx: "4", cy: "4", r: "4", fill: "currentColor" })
        }
      ) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive2.RadioItem.displayName;
var DropdownMenuLabel = React13.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive2.Label.displayName;
var DropdownMenuSeparator = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive2.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-slate-200 dark:bg-slate-700", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive2.Separator.displayName;
var DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
var Label3 = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(
      "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
      className
    ),
    ...props
  }
));
Label3.displayName = LabelPrimitive.Root.displayName;
var Switch = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SwitchPrimitive.Root,
  {
    className: cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-200 dark:focus-visible:ring-blue-400 dark:focus-visible:ring-offset-slate-900 dark:data-[state=checked]:bg-blue-500 dark:data-[state=unchecked]:bg-slate-700",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx(
      SwitchPrimitive.Thumb,
      {
        className: cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitive.Root.displayName;
var Separator4 = React13.forwardRef(({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx(
  SeparatorPrimitive.Root,
  {
    ref,
    decorative,
    orientation,
    className: cn(
      "shrink-0 bg-border",
      orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
      className
    ),
    ...props
  }
));
Separator4.displayName = SeparatorPrimitive.Root.displayName;
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React13.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "h5",
    {
      ref,
      className: cn("mb-1 font-medium leading-none tracking-tight", className),
      ...props
    }
  )
);
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("text-sm [&_p]:leading-relaxed", className), ...props }));
AlertDescription.displayName = "AlertDescription";
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(AccordionPrimitive.Item, { ref, className: cn("border-b", className), ...props }));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
  AccordionPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 transition-transform duration-200" })
    ]
  }
) }));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var AccordionContent = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Content,
  {
    ref,
    className: "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
  }
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
var Progress = React13.forwardRef(
  ({ className, value = 0, max = 100, ...props }, ref) => {
    const percentage = Math.min(Math.max(value / max * 100, 0), 100);
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        role: "progressbar",
        "aria-valuenow": value,
        "aria-valuemin": 0,
        "aria-valuemax": max,
        className: cn("relative h-4 w-full overflow-hidden rounded-full bg-secondary", className),
        ...props,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: "h-full w-full flex-1 bg-primary transition-all",
            style: { transform: `translateX(-${100 - percentage}%)` }
          }
        )
      }
    );
  }
);
Progress.displayName = "Progress";
var Table = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { className: "relative w-full overflow-auto", children: /* @__PURE__ */ jsx("table", { ref, className: cn("w-full caption-bottom text-sm", className), ...props }) })
);
Table.displayName = "Table";
var TableHeader = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("thead", { ref, className: cn("[&_tr]:border-b", className), ...props }));
TableHeader.displayName = "TableHeader";
var TableBody = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("tbody", { ref, className: cn("[&_tr:last-child]:border-0", className), ...props }));
TableBody.displayName = "TableBody";
var TableFooter = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tfoot",
  {
    ref,
    className: cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className),
    ...props
  }
));
TableFooter.displayName = "TableFooter";
var TableRow = React13.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "tr",
    {
      ref,
      className: cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      ...props
    }
  )
);
TableRow.displayName = "TableRow";
var TableHead = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "th",
  {
    ref,
    className: cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className
    ),
    ...props
  }
));
TableHead.displayName = "TableHead";
var TableCell = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "td",
  {
    ref,
    className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className),
    ...props
  }
));
TableCell.displayName = "TableCell";
var TableCaption = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("caption", { ref, className: cn("mt-4 text-sm text-muted-foreground", className), ...props }));
TableCaption.displayName = "TableCaption";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React13.forwardRef(({ className, sideOffset = 4, ...props }, ref) => (
  // Portaled so sticky / overflow ancestors (e.g. sticky table cells) can't paint over it.
  /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    TooltipPrimitive.Content,
    {
      ref,
      sideOffset,
      className: cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ...props
    }
  ) })
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
function defaultTime() {
  const d = /* @__PURE__ */ new Date();
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "" : "";
  h = h % 12 || 12;
  return `${h}:${m}${ampm}`;
}
var StatusBar = ({ time, className }) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex h-9 items-center justify-between px-7 pt-1.5 text-[13px] font-semibold",
        "text-slate-900 dark:text-white",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: time ?? defaultTime() }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(Signal, { className: "h-3.5 w-3.5", strokeWidth: 2.5, "aria-hidden": "true" }),
          /* @__PURE__ */ jsx(Wifi, { className: "h-3.5 w-3.5", strokeWidth: 2.5, "aria-hidden": "true" }),
          /* @__PURE__ */ jsx(BatteryFull, { className: "h-4 w-4", strokeWidth: 2, "aria-hidden": "true" })
        ] })
      ]
    }
  );
};
StatusBar.displayName = "StatusBar";
var SIZE_PX = {
  sm: 280,
  md: 320,
  lg: 375
};
var PhoneFrame = React13.forwardRef(
  ({
    size = "md",
    showStatusBar = true,
    showHomeIndicator = true,
    statusBarTime,
    screenClassName,
    className,
    style,
    children,
    ...rest
  }, ref) => {
    const width = SIZE_PX[size];
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          // Outer bezel
          "relative mx-auto rounded-[2.75rem] p-[10px]",
          "bg-gradient-to-b from-slate-800 via-slate-900 to-slate-950",
          "shadow-2xl shadow-slate-900/30",
          "ring-1 ring-slate-950/40",
          className
        ),
        style: { width, ...style },
        ...rest,
        children: /* @__PURE__ */ jsxs("div", { className: "relative overflow-hidden rounded-[2.15rem] bg-white dark:bg-slate-950", children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              "aria-hidden": "true",
              className: "pointer-events-none absolute left-1/2 top-2 z-20 h-7 w-28 -translate-x-1/2 rounded-full bg-black"
            }
          ),
          showStatusBar && /* @__PURE__ */ jsx("div", { className: "relative z-10", children: /* @__PURE__ */ jsx(StatusBar, { time: statusBarTime }) }),
          /* @__PURE__ */ jsx("div", { className: cn("relative z-0", screenClassName), children }),
          showHomeIndicator && /* @__PURE__ */ jsx(
            "div",
            {
              "aria-hidden": "true",
              className: "pointer-events-none absolute bottom-1.5 left-1/2 z-20 h-1 w-1/3 -translate-x-1/2 rounded-full bg-slate-900/80 dark:bg-white/70"
            }
          )
        ] })
      }
    );
  }
);
PhoneFrame.displayName = "PhoneFrame";
var SIZE_PX2 = {
  sm: 640,
  md: 800,
  lg: 1024
};
var DesktopFrame = React13.forwardRef(
  ({
    size = "md",
    url,
    showRefresh = true,
    showControls = true,
    screenClassName,
    className,
    style,
    children,
    ...rest
  }, ref) => {
    const width = SIZE_PX2[size];
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "relative mx-auto overflow-hidden rounded-xl",
          "border border-slate-200 bg-white",
          "shadow-2xl shadow-slate-900/10",
          "dark:border-slate-700 dark:bg-slate-900",
          className
        ),
        style: { width, ...style },
        ...rest,
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "flex h-10 items-center gap-3 border-b px-3",
                "border-slate-200 bg-slate-50",
                "dark:border-slate-700 dark:bg-slate-800/60"
              ),
              children: [
                showControls && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", "aria-hidden": "true", children: [
                  /* @__PURE__ */ jsx("span", { className: "h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-600" }),
                  /* @__PURE__ */ jsx("span", { className: "h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-600" }),
                  /* @__PURE__ */ jsx("span", { className: "h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-600" })
                ] }),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: cn(
                      "mx-auto flex h-6 max-w-[60%] flex-1 items-center gap-1.5 rounded-full px-3",
                      "bg-white text-[11px] text-slate-500",
                      "border border-slate-200",
                      "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
                    ),
                    children: [
                      /* @__PURE__ */ jsx(Lock, { className: "h-3 w-3 shrink-0", "aria-hidden": "true" }),
                      /* @__PURE__ */ jsx("span", { className: "truncate", children: url ?? "app.example.com" })
                    ]
                  }
                ),
                showRefresh && /* @__PURE__ */ jsx(
                  RotateCw,
                  {
                    className: "h-3.5 w-3.5 text-slate-400 dark:text-slate-500",
                    "aria-hidden": "true"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("div", { className: cn("relative min-h-[400px] bg-white dark:bg-slate-900", screenClassName), children })
        ]
      }
    );
  }
);
DesktopFrame.displayName = "DesktopFrame";
var DevicePreview = ({
  device,
  children,
  className,
  url,
  statusBarTime,
  phoneSize,
  desktopSize,
  screenClassName,
  hideStatusBar,
  hideHomeIndicator,
  hideControls,
  hideRefresh
}) => {
  if (device === "mobile") {
    return /* @__PURE__ */ jsx(
      PhoneFrame,
      {
        className,
        size: phoneSize,
        statusBarTime,
        showStatusBar: !hideStatusBar,
        showHomeIndicator: !hideHomeIndicator,
        screenClassName,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx(
    DesktopFrame,
    {
      className,
      size: desktopSize,
      url,
      showControls: !hideControls,
      showRefresh: !hideRefresh,
      screenClassName,
      children
    }
  );
};
DevicePreview.displayName = "DevicePreview";

export { AVATAR_COLORS, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertDescription, AlertTitle, AllowsToSendPill, Avatar, AvatarFallback, AvatarImage, Badge, BottomNav, Breadcrumbs, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, CollapsibleRuleSection, ContactsEmptyState, DataTable, DesktopFrame, DevicePreview, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DocumentsEmptyState, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EmptyState, GlobalSearch, InboxEmptyState, Input, JurisdictionPicker, Label3 as Label, LoadingInline, LoadingScreen, MobileBreadcrumb, Modal, MotionButton, NoDataState, NoResultsState, PhoneFrame, Progress, RadioCard, RadioGroup, RadioGroupItem, RuleArrow, RuleFlowCard, RuleFlowSectionHeader, SearchableSelect, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator4 as Separator, Sheet, SheetBody, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarItem, SidebarMobileTrigger, SidebarProvider, SidebarToggle, Skeleton, SkeletonAvatar, SkeletonCard, SkeletonListItem, SkeletonTableRow, SkeletonText, Spinner, StatusBadge, StatusBar, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, TagBadge, TagColorPicker, TagInput, TagList, Textarea, Toast, ToastAction, ToastClose, ToastDescription, ToastIcon, ToastProvider, ToastTitle, ToastViewport, Toaster, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TopNav, ViewToggle, badgeVariants, bottomSheetVariants, buttonPressVariants, buttonVariants, chartA11yProps, cn, collapseVariants, defaultTransition, fabVariants, fadeInVariants, fastTransition, getAccessibleTagColors, getAvatarColor, getAvatarInitials, modalVariants, overlayVariants, pageTransitionVariants, pieSegmentA11yProps, reducer, ruleFlowIconFor, searchDropdownVariants, searchOverlayVariants, searchResultItemVariants, searchResultsContainerVariants, slideUpVariants, staggerContainerVariants, staggerItemVariants, toast, toastVariants, useIsDesktop, useIsMobile, useIsTablet, useMediaQuery, useSidebar, useToast, useViewToggle };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map